import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ChangeEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Form } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { UserPlus } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Clock from "../components/Clock";
import Modal from "../components/Modal";
import Option from "../components/Option";
import useOverlayLoader from "../components/OverlayLoader/useOverlayLoader";
import ProductSearch from "../components/ProductSearch";
import { Dropdown } from "../components/PurchaseForm";
import { FormInput } from "../components/custom/components";
import UserLayout from "../components/custom/layouts/UserLayout";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import { apiUrl, FormType, FormTypeList } from "../config";
import api from "../config/api";
import {
  LocalFormatDate,
  convertAmtToWords,
  decodeHtml,
  getDate,
  isMobileNo,
  rupeeFormat,
} from "../config/functions";
import { AccountHolderType } from "../redux/actions/accountHolder";
import { AuthUserType } from "../redux/actions/authUserActions";
import { ClientRegistrationType } from "../redux/actions/clientRegistration";
import {
  CustomerType,
  addCustomerAction,
  fetchCustomerAction,
} from "../redux/actions/customerAction";
import {
  DropDownType,
  addDropDownAction,
  fetchDropDownAction,
} from "../redux/actions/dropdownAction";
import {
  SalesPaymentModeTypes,
  SalesPaymentModesDropDown,
  SalesType,
  addSalesAction,
  fetchSalesAction,
  updateSalesAction,
} from "../redux/actions/salesActions";
import {
  StockMasterType,
  fetchStockMasterAction,
} from "../redux/actions/stockMasterAction";
import { UsersType } from "../redux/actions/usersActions";
import { AppDispatch, StoreState } from "../redux/store";
import { getSettings } from "../services/appSetting.service";
import { getSalesById } from "../services/sales.service ";
import {
  DEFAULT_PLACE_OF_SUPPLY,
  resolvePlaceOfSupply,
  SOUTH_INDIA_PLACE_OF_SUPPLY_OPTIONS,
} from "../utils/placeOfSupplyOptions";
import { getTransactionCurrentBalanceByAcNo } from "../services/transaction.service";
import {
  isThermalPrinterFromSettings,
  postThermalSalesReceiptIfNeeded,
  type SalesPrintObject,
} from "../utils/thermalSalesReceiptPrint";
import Sales from "./Sales";
import { getCustomer } from "../services/customer.services";
import { BranchType } from "../redux/actions/branchAction";
import db, { IndexDBProduct, refreshProductsFromAPI } from "../db/db";

function thermalPaymentLabel(mode: SalesPaymentModeTypes): string {
  return mode === SalesPaymentModesDropDown.CASH ? "CASH SALES" : mode;
}

export interface SalesItem {
  id: number;
  productGroupCode: string;
  productId: number;
  productCode: string;
  productName: Dropdown | null;
  modelNumber: string;
  gstPercent?: string;
  qty: string;
  mrp?: string;
  rate?: string;
  disAmt?: string;
  pdPercent?: number;
  pdAmount?: number;
  gstValue?: string;
  taxable?: string;
  totalAmt?: string;
  hsn?: string;
  batchNo?: string;
  expiryDt?: Date | null;
  pwog?: string;
  pwg?: string;
  // sRate1?: string
  // sRate2?: string
  // sRate3?: string
  // pdRs?: string
  // cdPercent?: string
  // cdRs?: string
  igst?: string;
  cgst?: string;
  sgst?: string;
  // netAmt?: string
  // unitProfit?: string
  // totalProfit?: string
  profitPercent?: string;
}

interface Payments {
  cash?: number;
  card1?: number;
  card2?: number;
  online1?: number;
  online2?: number;
}

const SALSE_PAYMENT_MODES = [
  {
    text: "CASH",
    value: "Cash",
  },
  {
    text: "CREDIT",
    value: "Credit",
  },
  {
    text: "BANK",
    value: "Bank",
  },
];
const SALES_TYPE_DROPS = [
  {
    text: "SALES TAX INVOICE",
    value: "SALES TAX INVOICE",
  },
  {
    text: "SALES ESTIMATE",
    value: "SALES ESTIMATE",
  },
];

export const getDefaultSalesItems = () => {
  const salesItems: SalesItem[] = [];

  for (let i = 0; i < 100; i++) {
    salesItems.push({
      id: i + 1,
      productName: null,
      productId: 0,
      productGroupCode: "",
      productCode: "",
      modelNumber: "",
      batchNo: "",
      expiryDt: null,
      gstPercent: "",
      qty: "",
      mrp: "0",
      rate: "0",
      disAmt: "0",
      pdPercent: 0,
      pdAmount: 0,
      gstValue: "0",
      taxable: "0",
      totalAmt: "0",
      hsn: "",
      igst: "",
      cgst: "",
      sgst: "",
      pwg: "",
      pwog: "",
      profitPercent: "0",
    });
  }
  return salesItems;
};

/** Normalizes API row keys (snake_case vs camelCase) for sales line items. */
function salesItemRowCodes(pItem: SalesItemEditAttributes): {
  productCode: string;
  batchNumber: string;
} {
  const row = pItem as unknown as Record<string, unknown>;
  const productCode =
    row.product_code != null
      ? String(row.product_code)
      : row.productCode != null
        ? String(row.productCode)
        : "";
  const batchNumber =
    row.batch_number != null
      ? String(row.batch_number)
      : row.batchNumber != null
        ? String(row.batchNumber)
        : "";
  return { productCode, batchNumber };
}

/**
 * `getSingleSales` returns plain `salesitems` rows (no model column). ProductSearch
 * resolves model from Dexie; edit flow should use the same source when stock Redux
 * omits `modelNumber`.
 */
async function resolveSalesItemModelNumber(
  pItem: SalesItemEditAttributes,
  stockObj: StockMasterType | undefined,
): Promise<string> {
  const row = pItem as unknown as Record<string, unknown>;
  const raw =
    row.model_number ?? row.modelNumber ?? row.model_no ?? row.modelNo;
  if (raw !== undefined && raw !== null && String(raw) !== "") {
    return String(raw);
  }
  const { productCode, batchNumber } = salesItemRowCodes(pItem);
  if (productCode) {
    try {
      const matches = await db.products
        .where("productCode")
        .equals(productCode)
        .toArray();
      if (matches.length) {
        const byBatch = matches.find(
          (m) => String(m.batchNumber ?? "") === batchNumber,
        );
        const hit = byBatch ?? matches[0];
        const m = hit?.modelNumber;
        if (m !== undefined && m !== null && String(m) !== "") {
          return String(m);
        }
      }
    } catch {
      /* ignore Dexie errors */
    }
  }
  return stockObj?.modelNumber?.toString() ?? "";
}

export interface SalesItemEditAttributes {
  id: number;
  sales_id: number;
  entry_number: number;
  product_id: number;
  product_code: string;
  model_number:string;
  batch_number: number;
  expiry_date: string;
  quantity: number;
  free_quantity: number;
  total_quantity: number;
  price_wogst: number;
  price_gst: number;
  hsn: string;
  w_rate: number;
  r_rate: number;
  l_rate: number;
  mrp: number;
  gst_value: number;
  s_discount: number;
  p_discount_percent: number;
  p_discount_amount: number;
  c_discount_percent: number;
  c_discount_amount: number;
  taxable_amount: number;
  igst_amount: number;
  cgst_amount: number;
  sgst_amount: number;
  total_amount: number;
  unit_cost: number;
  profit_percent: number;
  profit_amount: number;
}

export interface SalesEditAttributes {
  id: number;
  finance_year: string;
  branch_id: number;
  invoice_type: string;
  invoice_title: string;
  invoice_date: string;
  invoice_day: number;
  invoice_week_number: number;
  invoice_week_day: number;
  invoice_month: number;
  invoice_year: number;
  invoice_number: number;
  sales_entry_number: number;
  system_no: string;
  place_of_supply?: string;
  rep_id: number;
  customer_id: number;
  bill_amount: string;
  dis_percent: string;
  dis_amount: string;
  total_amount: string;
  total_discount: string;
  prev_balance: string;
  credit: string;
  bank_received: string;
  add: string;
  less: string;
  grand_total: string;
  amt_in_words: string;
  particulars: string;
  amt_paid: string;
  payment_mode: string;
  delivery_mode: string;
  delivery_details: string;
  remarks: string;
  tender_return: number;
  tender_amount: number;
  net_taxable: string;
  net_igst: string;
  net_sgst: string;
  net_cgst: string;
  net_profit: string;
  net_profit_percent: string;
  A_value: number;
  A_taxable: number;
  B_value: number;
  B_taxable: number;
  C_value: number;
  C_taxable: number;
  D_value: number;
  D_taxable: number;
  E_value: number;
  E_taxable: number;
  F_value: number;
  F_taxable: number;
  G_value: number;
  G_taxable: number;
  createdAt?: string;
  created_by: number;
  split: string;
  salesItems: SalesItemEditAttributes[];
  Transactions?: any[];
}

export interface CDropdown {
  label: string;
  name: string;
  value: number;
  score: string;
  conNo: string;
  conNo2: string;
  gstNo: string;
  place: string;
}

const SalesMaster = () => {
  const toast = useToast();
  const [setShowOverlayLoader, setHideOverlayLoader] = useOverlayLoader();
  const dispatch = useDispatch<AppDispatch>();
  const AuthUser = useSelector<StoreState, AuthUserType | null>(
    (state) => state.authUser,
  );
  const StockList = useSelector<StoreState, StockMasterType[]>(
    (state) => state.stockMaster,
  );
  const AccountLists = useSelector<StoreState, AccountHolderType[]>(
    (state) => state.account,
  );
  const userList = useSelector<StoreState, UsersType[]>((state) => state.user);
  const DropDownList = useSelector<StoreState, DropDownType[]>(
    (state) => state.dropdown,
  );
  const BranchList = useSelector<StoreState, BranchType[]>(
    (state) => state.branch,
  );

  const [customerList, setCustomerList] = useState<CustomerType[]>([]);

  const clients = useSelector<StoreState, ClientRegistrationType[]>(
    (state) => state.clientRegistration,
  );
  const [discountValue, setDiscountValue] = useState<string>("0");
  const [placeOfSupply, setPlaceOfSupply] = useState<string>(DEFAULT_PLACE_OF_SUPPLY);

  const fetchCustomer = async () => {
    const res = await getCustomer();
    setCustomerList(res.data.data);
  };
  useEffect(() => {
    dispatch(fetchDropDownAction());
    // dispatch(fetchStockMasterAction());
    fetchCustomer();
  }, []);

  const [financialYear, setFinancialYear] = useState<string>("");
  // const [financialYearError, setFinancialYearError] = useState<string>('')
  const [billNo, setBillNo] = useState<string>();
  // const [billNoError, setBillNoError] = useState<string>('')
  const [billTime, setBillTime] = useState<string>("");
  // const [billTimeError, setBillTimeError] = useState<string>('')
  const [gudsRecdDate, setGudsRecdDate] = useState<string>("");
  const [billDate, setBillDate] = useState<Date>(new Date());
  // const [billDateError, setBillDateError] = useState<string>('')
  // const [customerNo, setCustomerNo] = useState<string>('2')
  const [invoiceType, setInvoiceType] = useState<string>(
    SALES_TYPE_DROPS[0].value,
  );
  const [invoiceTypeError, setInvoiceTypeError] = useState<string>("");
  const [invoiceTitle, setInvoiceTitle] = useState<string>("RETAIL");
  // const [invoiceTitleError, setInvoiceTitleError] = useState<string>('')

  const [invoiceAmount, setInvoiceAmount] = useState<string>("");
  const [salesEntryNo, setSalesEntryNo] = useState<string>(
    (Math.floor(Math.random() * 60000) + 10000).toString(),
  );
  const [customer, setCustomer] = useState<CDropdown | null>(null);

  const [customerName, setCustomerName] = useState<string>("");
  const [customerNameError, setCustomerNameError] = useState<string>("");
  const [customerAddress, setCustomerAddress] = useState<string>("");
  // const [customerAddressError, setCustomerAddressError] = useState<string>('')
  const [customerContact, setCustomerContact] = useState<string>("");
  const [customerContact2, setCustomerContact2] = useState<string>("");

  // const [customerContactError, setCustomerContactError] = useState<string>('')
  const [gst, setGST] = useState<string>("");
  const [gstError, setGSTError] = useState<string>("");

  const [refBy, setRefBy] = useState<string>("0");
  const [refByError, setRefByError] = useState<string>("");
  const [customerPlace, setCustomerPlace] = useState<string>("");
  // const [customerPlaceError, setCustomerPlaceError] = useState<string>('')
  const [branch, setBranch] = useState<string>("");
  // const [branchError, setBranchError] = useState<string>('')
  const [billedBy, setBilledBy] = useState<string>(
    AuthUser?.name ? AuthUser.name : "",
  );
  // const [billedByError, setBilledByError] = useState<string>('')
  const [sysNo, setSysNo] = useState<string>(window.location.hostname);
  // const [sysNoError, setSysNoError] = useState<string>('')

  // const [productName, setProductName] = useState<string>('')
  const [productNameError, setProductNameError] = useState<string>("");

  const [salesItems, setSalesItems] = useState<SalesItem[]>(
    getDefaultSalesItems(),
  );
  // const authUser = useSelector<StoreState, AuthUserType | null>(state => state.authUser)

  // const [showSalesForm, setShowSalesForm] = useState<boolean>(false)
  const [showSalesView, setShowSalesView] = useState<boolean>(false);

  const [billAmount, setBillAmount] = useState<string>("");
  const [billAmountError, setBillAmountError] = useState<string>("");
  const [disPercent, setDisPercent] = useState<string>("");
  const [disPercentError, setDisPercentError] = useState<string>("");
  const [disAmt, setDisAmt] = useState<string>("0");
  const [disAmtError, setDisAmtError] = useState<string>("");
  const [totAmt, setTotAmt] = useState<string>("");
  const [totAmtError, setTotAmtError] = useState<string>("");
  const [billNetAmt, setBillNetAmt] = useState<string>("");
  const [billNetAmtError, setBillNetAmtError] = useState<string>("");
  const [add, setAdd] = useState<string>("0");
  const [addError, setAddError] = useState<string>("");
  const [less, setLess] = useState<string>("0");
  const [lessError, setLessError] = useState<string>("");
  const [netAmt, setNetAmt] = useState<string>("0");
  const [netAmtError, setNetAmtError] = useState<string>("");
  const [roundOff, setRoundOff] = useState<string>("");
  const [roundOffError, setRoundOffError] = useState<string>("");
  const [gtotal, setGTotal] = useState<string>("0");
  const [gtotalError, setGTotalError] = useState<string>("");
  const [amtInWords, setAmtInWords] = useState<string>("");
  const [amtInWordsError, setAmtInWordsError] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState<SalesPaymentModeTypes>(
    SalesPaymentModesDropDown.CASH,
  );
  const [paymentModeError, setPaymentModeError] = useState<string>("");
  const [paymentNo, setPaymentNo] = useState<string>("");
  const [paymentNoError, setPaymentNoError] = useState<string>("");
  const [amtPaid, setAmtPaid] = useState<string>("0");
  const [amtPaidError, setAmtPaidError] = useState<string>("");
  const [balance, setBalance] = useState<string>("");
  const [balanceError, setBalanceError] = useState<string>("");
  const [deliveryMode, setDeliveryMode] = useState<string>("");
  // const [deliveryModeError, setDeliveryModeError] = useState<string>('')
  const [deliveryDetails, setDeliveryDetails] = useState<string>("");
  // const [deliveryDetailsError, setDeliveryDetailsError] = useState<string>('')
  const [currentBal, setCurrentBal] = useState<string>("0");
  const [currentBalError, setCurrentBalError] = useState<string>("");
  const [previousBal, setPreviousBal] = useState<string>("0");
  const [previousBalError, setPreviousBalError] = useState<string>("");
  const [overAllBal, setOverAllBal] = useState<string>("0");
  const [overAllBalError, setOverAllBalError] = useState<string>("");
  const [bankName, setBankName] = useState<string>("");
  const [bankNumber, setBankNumber] = useState<string>("");

  const [totalItems, setTotalItems] = useState<string>("");
  const [totalItemsError, setTotalItemsError] = useState<string>("");
  const [totalQty, setTotalQty] = useState<string>("");
  const [totalQtyError, setTotalQtyError] = useState<string>("");
  const [packNo, setPackNo] = useState<string>("");
  const [packNoError, setPackNoError] = useState<string>("");
  const [yourSavings, setYourSavings] = useState<string>("");
  const [yourSavingsError, setYourSavingsError] = useState<string>("");
  const [netIgst, setNetIgst] = useState<string>("0");
  const [netIgstError, setNetIgstError] = useState<string>("");
  const [netSgst, setNetSgst] = useState<string>("");
  const [netSgstError, setNetSgstError] = useState<string>("");
  const [netCgst, setNetCgst] = useState<string>("");
  const [netCgstError, setNetCgstError] = useState<string>("");
  const [netTaxable, setNetTaxable] = useState<string>("");
  const [netTaxableError, setNetTaxableError] = useState<string>("");
  const [netGSTValue, setNetGSTValue] = useState<string>("");
  const [netGSTValueError, setNetGSTValueError] = useState<string>("");

  const [tenderedAmt, setTenderedAmt] = useState<string>("");
  const [tenderedAmtError, setTenderedAmtError] = useState<string>("");
  const [tenderedReturn, setTenderedReturn] = useState<string>("");
  const [tenderedReturnError, setTenderedReturnError] = useState<string>("");
  const [tenderedBal, setTenderedBal] = useState<string>("");
  const [tenderedBalError, setTenderedBalError] = useState<string>("");

  const [finalAmount, setFinalAmount] = useState<string>("0");
  const [totalProfit, setTotalProfit] = useState<string>("0");

  const [formType, setFormType] = useState<FormType>(FormTypeList.ADD);
  const [id, setId] = useState<number>();

  const [modal, setModal] = useState(false);

  const [branchId, setBranchId] = useState<string>("");
  const [branchIdError, setBranchIdError] = useState<string>("");
  const [nameTag, setNameTag] = useState<string>("");
  const [nameTagError, setNameTagError] = useState<string>("");
  const [accountName, setAccountName] = useState<string>("");
  const [accountNameError, setAccountNameError] = useState<string>("");
  const [address1, setAddress1] = useState<string>("");
  const [address1Error, setAddress1Error] = useState<string>("");
  const [address2, setAddress2] = useState<string>("");
  const [address2Error, setAddress2Error] = useState<string>("");
  const [area, setArea] = useState<string>("");
  const [areaError, setAreaError] = useState<string>("");
  const [place, setPlace] = useState<string>("");
  const [placeError, setPlaceError] = useState<string>("");
  const [district, setDistrict] = useState<string>("");
  const [districtError, setDistrictError] = useState<string>("");
  const [pincode, setPincode] = useState<string | undefined>("");
  const [pincodeError, setPincodeError] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [stateError, setStateError] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [countryError, setCountryError] = useState<string>("");
  const [route, setRoute] = useState<string>("");
  const [routeError, setRouteError] = useState<string>("");
  const [contactNumber1, setContactNumber1] = useState<string | undefined>("");
  const [contactNumber1Error, setContactNumber1Error] = useState<string>("");
  const [contactNumber2, setContactNumber2] = useState<string | undefined>("");
  const [contactNumber2Error, setContactNumber2Error] = useState<string>("");
  const [aadharNumber, setAadharNumber] = useState<string | undefined>("");
  const [aadharNumberError, setAadharNumberError] = useState<string>("");
  const [gstNumber, setGstNumber] = useState<string | undefined>("");
  const [gstNumberError, setGstNumberError] = useState<string>("");

  const [hiddenGst, setHiddenGst] = useState<boolean>(true);

  const [areaName, setAreaName] = useState<string>("");
  const [areaModal, setAreaModal] = useState(false);

  const [placeName, setPlaceName] = useState<string>("");
  const [placeModal, setPlaceModal] = useState(false);

  const [routeName, setRouteName] = useState<string>("");
  const [routeModal, setRouteModal] = useState(false);

  const [districtName, setDistrictName] = useState<string>("");
  const [districtModal, setDistrictModal] = useState(false);

  const [stateName, setStateName] = useState<string>("");
  const [stateModal, setStateModal] = useState(false);

  const [countryName, setCountryName] = useState<string>("");
  const [countryModal, setCountryModal] = useState(false);

  const [previousPoints, setPreviousPoints] = useState<number>(0);
  const [pointsEarned, setPointsEarned] = useState<number>(
    Math.ceil(parseInt(finalAmount) / 100),
  );
  const [totalpoints, setTotalPoints] = useState<number>(0);
  const [isCustPointsRedeemed, setIsCustPointsRedeemed] =
    useState<boolean>(false);

  const [showTenderModal, setShowTenderModal] = useState(false);
  const [cash, setCash] = useState<string>("");
  const [card1, setCard1] = useState<string>("");
  const [card2, setCard2] = useState<string>("");
  const [onlinePayment1, setOnlinePayment1] = useState<string>("");
  const [onlinePayment2, setOnlinePayment2] = useState<string>("");
  const [split, setSplit] = useState<string>("");
  const [remark, setRemark] = useState<string>("");
  const [bankRecevied, setBankRecevied] = useState<string>("");
  const [ledgerBal, setLedgerBal] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("cashAndBank");
  const [cashEditable, setCashEditable] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [options, setOptions] = useState<string>("print");
  const [printObjectState, setPrintObjectState] = useState<any | null>(null);
  // const handleTimeChange = (newTime: string) => {
  //     setBillTime(newTime);
  // };
  const [TotalBillAmt, setTotalBillAmt] = useState<string>("0");
  const [TotalDisAmt, setTotalDisAmt] = useState<string>("0");

  //reset in ProductSearch
  const [resetSearch, setResetSearch] = useState<boolean>(false);

  //focus
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const addRef = useRef<HTMLInputElement>(null);
  const lessRef = useRef<HTMLInputElement>(null);

  const tenderRef = useRef<any>(null);
  const cashRef = useRef<any>(null);
  const card1Ref = useRef<any>(null);
  const card2Ref = useRef<any>(null);
  const online1Ref = useRef<any>(null);
  const online2Ref = useRef<any>(null);
  const productName = useRef<any>(null);
  const qtyRef = useRef<any>(null);

  const handleKeyDownForRight = <T extends Element>(
    e: React.KeyboardEvent<T>,
    nextRef: React.RefObject<
      | HTMLDivElement
      | HTMLInputElement
      | HTMLSelectElement
      | HTMLTextAreaElement
    >,
    fieldName: string,
  ) => {
    if (e.key === "Enter" && nextRef.current) {
      e.preventDefault();
      const input = nextRef.current.querySelector(
        "input, select, .react-select__input",
      );
      if (input) {
        (input as HTMLElement).focus();
      }
      setFocusedField(fieldName);
    }
  };

  const refs = useRef<{
    [key: string]: any;
  }>({});

  const handleKeyDown = (
    e: React.KeyboardEvent | React.MouseEvent,
    nextField: string,
    rowId: string,
    moveToNextRow: boolean = false,
  ) => {
    const isKeyboardEvent = e.type === "keydown";
    const isMouseEvent = e.type === "click";

    if (isKeyboardEvent && (e as React.KeyboardEvent).key === "Enter") {
      e.preventDefault();

      const currentRow = parseInt(rowId, 10);
      const targetRowId = moveToNextRow ? currentRow + 1 : currentRow;
      const nextRef = refs.current[`${nextField}-${targetRowId}`];

      if (nextRef) {
        if (nextRef.querySelector) {
          const input = nextRef.querySelector(
            "input, select, .react-select__input",
          );
          if (input) {
            (input as HTMLElement).focus();
          }
        } else if (nextRef.focus) {
          nextRef.focus();
        } else if (nextRef.select?.focus) {
          nextRef.select.focus();
        }

        setFocusedField(`${nextField}-${targetRowId}`);
      }
    } else if (isMouseEvent) {
      e.preventDefault();

      const currentRow = parseInt(rowId, 10);
      const targetRowId = moveToNextRow ? currentRow + 1 : currentRow;
      const nextRef = refs.current[`${nextField}-${targetRowId}`];

      if (nextRef) {
        if (nextRef.querySelector) {
          const input = nextRef.querySelector(
            "input, select, .react-select__input",
          );
          if (input) {
            (input as HTMLElement).focus();
          }
        } else if (nextRef.focus) {
          nextRef.focus();
        } else if (nextRef.select?.focus) {
          nextRef.select.focus();
        }

        setFocusedField(`${nextField}-${targetRowId}`);
      }
    }
  };

  useEffect(() => {
    const firstItemId = salesItems[0]?.id;

    if (firstItemId && refs.current[`productName-${firstItemId}`]) {
      const currentRef = refs.current[`productName-${firstItemId}`];

      if (currentRef && currentRef.focus) {
        // currentRef.focus();
        setFocusedField(`productName-${firstItemId}`);
      }
    }
  }, [salesItems]);

  // useEffect(() => {
  //     let total = 0;
  //     let disTotal = 0;
  //     salesItems
  //         ?.filter((f) => {
  //             return (
  //                 f.productName?.label !== "" &&
  //                 f.productName?.label !== undefined &&
  //                 f.productName?.value !== "" &&
  //                 f.productName?.value !== undefined
  //             );
  //         })
  //         .map((si) => {
  //             total += parseFloat(si.totalAmt ? si.totalAmt : "0");
  //             disTotal += si.pdAmount ? si.pdAmount : 0;
  //         });
  //         console.log('tot',total);
  //         console.log('tot dis', disTotal);

  //     setTotalBillAmt(total?.toFixed(2));
  //     setTotalDisAmt(disTotal?.toFixed(2));
  // }, [salesItems]);
  // useEffect(() => {
  //     let total = 0;
  //     salesItems
  //         ?.filter((f) => {
  //             return (
  //                 f.productName?.label !== "" &&
  //                 f.productName?.label !== undefined &&
  //                 f.productName?.value !== "" &&
  //                 f.productName?.value !== undefined
  //             );
  //         })
  //         .map((si) => {
  //             // Use MRP * Qty instead of totalAmt which might have included discount logic earlier
  //             const itemTotal = parseFloat(si.mrp || "0") * parseFloat(si.qty || "0");
  //             total += itemTotal;
  //         });
  //     setTotalBillAmt(total?.toFixed(2));
  //     // Optionally keep TotalDisAmt as 0 or remove it from UI if not needed
  //     setTotalDisAmt("0"); // or keep existing if you still want to show discount separately
  // }, [salesItems]);

  // useEffect(() => {
  //     // Step 1: Sum of (qty × mrp) for all valid items
  //     const baseTotal = salesItems
  //         .filter(f => f.productName?.value)
  //         .reduce((sum, item) => {
  //             return sum + (parseFloat(item.qty || "0") * parseFloat(item.mrp || "0"));
  //         }, 0);

  //     // Step 2: Apply Add and Less
  //     const adjustedTotal = baseTotal + parseFloat(add || "0") - parseFloat(less || "0");

  //     // Step 3: Round to nearest integer (as per your requirement)
  //     const roundedTotal = Math.round(adjustedTotal);

  //     setGTotal(roundedTotal.toString());
  //     setFinalAmount(roundedTotal.toString());
  //     setNetAmt(adjustedTotal.toFixed(2)); // keep precise for internal use
  // }, [salesItems, add, less]);
  useEffect(() => {
    // Sum of all line totalAmt (after discount)
    const baseTotal = salesItems
      .filter((f) => f.productName?.value)
      .reduce((sum, item) => {
        return sum + parseFloat(item.totalAmt || "0");
      }, 0);

    // Apply Add / Less
    const adjustedTotal =
      baseTotal + parseFloat(add || "0") - parseFloat(less || "0");
    const roundedTotal = Math.round(adjustedTotal);

    setGTotal(roundedTotal.toString());
    setFinalAmount(roundedTotal.toString());
    setNetAmt(adjustedTotal.toFixed(2));
  }, [salesItems, add, less]);

  useEffect(() => {
    let defaultCustomer = customerList?.find(
      (a) => a.accountName?.toLowerCase() === "cash sales",
    );
    if (defaultCustomer) {
      let CusObj = {
        label:
          defaultCustomer?.accountName! +
            "-" +
            defaultCustomer?.contactNumber1 || "",
        name: defaultCustomer?.accountName ? defaultCustomer?.accountName : "",
        value: defaultCustomer?.accountNumber || 0,
        score: defaultCustomer?.score
          ? defaultCustomer?.score?.toString()
          : "0",
        conNo: defaultCustomer?.contactNumber1
          ? defaultCustomer?.contactNumber1?.toString()
          : "",
        conNo2: defaultCustomer?.contactNumber2
          ? defaultCustomer?.contactNumber2?.toString()
          : "",
        gstNo: "Nil",
        place: ""
      };
      setCustomer(CusObj);
      setCustomerName(
        defaultCustomer?.accountName ? defaultCustomer.accountName : "",
      );
      setCustomerContact(defaultCustomer?.contactNumber1?.toString()!);
      setPreviousPoints(
        defaultCustomer?.score ? parseInt(defaultCustomer?.score!) : 0,
      );
    }
    let defaultRepresenter = AccountLists?.find(
      (al) =>
        al.groupName?.toLowerCase() === "representer" &&
        al.accountName?.toLowerCase() === "self",
    );
    setRefBy(defaultRepresenter?.accountNumber?.toString()!);

    let defaultBranch = AuthUser?.branchId;
    setBranchId(defaultBranch ? defaultBranch?.toString()! : "");
    let defAddress = clients?.find(
      (cl) => cl.id?.toString() === AuthUser?.branchId?.toString(),
    );
    let defPlace = DropDownList?.filter(
      (ddl: DropDownType) => ddl.category === "place",
    )
      ?.find(
        (ddl: DropDownType) =>
          ddl.title?.toLowerCase() === defAddress?.place?.toLowerCase(),
      )
      ?.id?.toString();
    let defDistrict = DropDownList?.filter(
      (ddl: DropDownType) => ddl.category === "district",
    )
      ?.find(
        (ddl: DropDownType) =>
          ddl.title?.toLowerCase() === defAddress?.district?.toLowerCase(),
      )
      ?.id?.toString();
    let defState = DropDownList?.filter(
      (ddl: DropDownType) => ddl.category === "state",
    )
      ?.find(
        (ddl: DropDownType) =>
          ddl.title?.toLowerCase() === defAddress?.state?.toLowerCase(),
      )
      ?.id?.toString();

    setPlace(defPlace ? defPlace : "");
    setDistrict(defDistrict ? defDistrict : "");
    setState(defState ? defState : "");
    setCountry("India");
  }, [customerList, AccountLists, AuthUser, clients, DropDownList]);

  const resetStates = () => {
    fetchRecentSalesId();
    setFormType(FormTypeList.ADD);
    setSalesItems(getDefaultSalesItems());
    // setPreviousPoints(0)
    setPointsEarned(0);
    setTotalPoints(0);
    setDiscountValue("");
    setPlaceOfSupply(DEFAULT_PLACE_OF_SUPPLY);
    // setPackNo()
    setInvoiceType(SALES_TYPE_DROPS[0].value);
    setTenderedAmt("");
    setTenderedReturn("");
    setTenderedBal("");
    //top
    setGudsRecdDate("");
    setBillNo("");
    setBillTime("");
    setBillDate(new Date());
    // setCustomerNo('')
    setInvoiceAmount("");
    setSalesEntryNo("");
    // setCustomer(null)
    // setCustomerName('')
    // setCustomerAddress('')
    // setCustomerContact('')
    setGST("");
    // setRefBy('')
    setCustomerPlace("");
    setBranch("");
    setBilledBy("");
    setSysNo(window.location.hostname);
    //
    setBillAmount("");
    setBillAmountError("");
    setDisPercent("");
    setDisPercentError("");
    setDisAmt("0");
    setDisAmtError("");
    setTotAmt("");
    setTotAmtError("");
    setRoundOff("0");
    setRoundOffError("");
    setBillNetAmt("");
    setBillNetAmtError("");
    setAdd("0");
    setAddError("");
    setLess("0");
    setLessError("");
    setNetAmt("0");
    setNetAmtError("");
    setGTotal("0");
    setGTotalError("");
    setAmtInWords("");
    setAmtInWordsError("");
    setPaymentMode(SalesPaymentModesDropDown.CASH);
    setPaymentModeError("");
    setPaymentNo("");
    setPaymentNoError("");
    setAmtPaid("0");
    setAmtPaidError("");
    setCurrentBal("0");
    setCurrentBalError("");
    setPreviousBal("0");
    setPreviousBalError("");
    setOverAllBal("0");
    setOverAllBalError("");
    setBankName("");
    setBankNumber("");
    setTenderedAmt("0");
    setTenderedAmtError("");
    setTenderedBal("0");
    setTenderedBalError("");
    setTotalItems("");
    setTotalItemsError("");
    setTotalQty("");
    setTotalQtyError("");
    setFinalAmount("0");
    // setBranchId('')
    setNameTag("");
    setAccountName("");
    setContactNumber1("");
    setContactNumber2("");
    setAddress1("");
    setAddress2("");
    setArea("");
    // setPlace('')
    // setDistrict('')
    // setState('')
    // setCountry('')
    setRoute("");
    setAadharNumber("");
    setGstNumber("");
    setAreaName("");
    setPlaceName("");
    setRouteName("");
    setDistrictName("");
    setStateName("");
    setCountryName("");

    setCash("");
    setCard1("");
    setCard2("");
    setOnlinePayment1("");
    setOnlinePayment2("");
    setSplit("");
    setRemark("");
    setShowTenderModal(false);

    // reset the product search
    setResetSearch(true);
  };

  const resetBill = () => {
    setCashEditable(false);
    setTenderedAmt("");
    setCash("");
    setCard1("");
    setCard2("");
    setOnlinePayment1("");
    setOnlinePayment2("");
    setSplit("No");
    setRemark("");
  };

  const handleRefBy: ChangeEventHandler<HTMLInputElement> = (e) => {
    setRefBy(e.target.value);
    setRefByError("");
  };

  const handleInvoiceType: ChangeEventHandler<HTMLInputElement> = (e) => {
    setInvoiceType(e.target.value);
    setInvoiceTypeError("");
  };

  // const handleProductName = (val: Dropdown | null, id: string) => {
  //     // console.log(val);
  //     // console.log(id);
  //     // const prodObj = ProductList.find(pd => pd.id?.toString() === val?.value?.toString()
  //     const stockObj = StockList.find(pd => pd.id?.toString() === val?.value?.toString()
  //     )

  //     const values: string[] | undefined = val?.label.split("-") || [''];

  //     let updated = [...salesItems]
  //     let ind = updated.findIndex(pi => pi.id.toString() === id)
  //     const existSalesItem = updated.findIndex(pi => pi.productName?.value === val?.value)
  //     if (existSalesItem > -1 && val !== null) {
  //         let qty = parseInt(updated[existSalesItem]?.qty) + 1;
  //         updated[existSalesItem].qty = qty.toString();
  //         let gval = (((qty ? parseFloat(qty.toString()) : 0) * (updated[existSalesItem]?.rate ? parseFloat(updated[existSalesItem]?.rate!) : 0)) - (updated[existSalesItem].disAmt ? parseFloat(updated[existSalesItem]?.disAmt!) : 0)) * ((updated[existSalesItem].gstPercent ? parseFloat(updated[existSalesItem]?.gstPercent!) : 0) / (100 + (updated[existSalesItem].gstPercent ? parseFloat(updated[existSalesItem]?.gstPercent!) : 0)))
  //         updated[existSalesItem].gstValue = gval?.toFixed(2)

  //         //TAXABLE((qty*rate) - disc) - GST value
  //         let tax = (((qty ? parseFloat(qty.toString()) : 0) * (updated[existSalesItem].rate ? parseFloat(updated[existSalesItem].rate!) : 0)) - (updated[existSalesItem].disAmt ? parseFloat(updated[existSalesItem].disAmt!) : 0)) - gval
  //         updated[existSalesItem].taxable = tax?.toFixed(2)

  //         ///TOTAL gst val+tax
  //         let tot = gval + tax
  //         updated[existSalesItem].totalAmt = tot?.toFixed(2)
  //         setSalesItems(updated)
  //         return
  //     }

  //     if (ind > -1) {
  //         if (val === null) {
  //             updated[ind] = {
  //                 ...updated[ind],
  //                 productName: null,
  //                 productCode: '',
  //                 mrp: '0',
  //                 rate: '0',
  //                 qty: '',
  //                 disAmt: '0',
  //                 pdPercent: 0,
  //                 pdAmount: 0,
  //                 totalAmt: '0.00'
  //             };
  //             setSalesItems(updated);
  //             setProductNameError('');
  //             return;
  //         }
  //         updated[ind].productName = val
  //         updated[ind].productCode = stockObj?.productCode ? stockObj?.productCode.toString() : ''
  //         updated[ind].productGroupCode = stockObj?.productGroupCode ? stockObj?.productGroupCode.toString() : ''
  //         // updated[ind].hsn = stockObj?.sac ? stockObj?.sac.toString() : ''
  //         updated[ind].gstPercent = stockObj?.gst ? stockObj?.gst.toString() : ''
  //         updated[ind].qty = '1'
  //         updated[ind].mrp = stockObj?.mrp ? stockObj?.mrp.toString() : ''
  //         updated[ind].rate = stockObj?.lRate ? stockObj?.lRate.toString() : ''
  //         updated[ind].hsn = stockObj?.sac ? stockObj?.sac.toString() : ''

  //         updated[ind].batchNo = stockObj?.batchNumber ? stockObj?.batchNumber.toString() : ''
  //         updated[ind].expiryDt = stockObj?.expiryDate ? new Date(stockObj?.expiryDate) : null
  //         updated[ind].igst = stockObj?.igstAmount ? stockObj?.igstAmount.toString() : ''
  //         updated[ind].cgst = stockObj?.cgstAmount ? stockObj?.cgstAmount.toString() : ''
  //         updated[ind].sgst = stockObj?.sgstAmount ? stockObj?.sgstAmount.toString() : ''
  //         updated[ind].pwog = stockObj?.priceWogst ? stockObj?.priceWogst.toString() : ''
  //         updated[ind].pwg = stockObj?.priceGst ? stockObj?.priceGst.toString() : ''

  //         //for unitprofit
  //         let uprofit = (stockObj?.wRate ? (stockObj?.wRate) : 0) - (stockObj?.priceWogst ? (stockObj?.priceWogst) : 0)
  //         // updated[ind].unitProfit = uprofit.toString()

  //         //for profit %
  //         let profitPercent = ((uprofit ? uprofit : 0) / (stockObj?.priceWogst ? (stockObj?.priceWogst) : 0)) * 100
  //         updated[ind].profitPercent = profitPercent.toFixed(2)

  //         //GST VALUE ((qty  rate) - disc )   GST/(100 + GST)
  //         let gval = (((updated[ind].qty ? parseFloat(updated[ind].qty) : 0) * (updated[ind].rate ? parseFloat(updated[ind].rate!) : 0)) - (updated[ind].disAmt ? parseFloat(updated[ind].disAmt!) : 0)) * ((stockObj?.gst ? parseFloat(stockObj?.gst.toString()) : 0) / (100 + (stockObj?.gst ? parseFloat(stockObj?.gst.toString()) : 0)))
  //         updated[ind].gstValue = gval?.toFixed(2)

  //         //TAXABLE((qty*rate) - disc) - GST value
  //         let tax = (((updated[ind].qty ? parseFloat(updated[ind].qty) : 0) * (updated[ind].rate ? parseFloat(updated[ind].rate!) : 0)) - (updated[ind].disAmt ? parseFloat(updated[ind].disAmt!) : 0)) - gval
  //         updated[ind].taxable = tax?.toFixed(2)

  //         ///TOTAL gstval +tax
  //         let tot = gval + tax
  //         updated[ind].totalAmt = tot?.toFixed(2)
  //         setSalesItems(updated)

  //         //Disount =totalAmount-rateAmount
  //         let dis = ((stockObj?.mrp ? stockObj?.mrp : 0) - (stockObj?.lRate ? stockObj?.lRate : 0))
  //         updated[ind].disAmt = dis?.toFixed(2)
  //     }
  //     if (val === null) {
  //         // updated[ind].expiryDt = null
  //         updated[ind].qty = ''
  //     }
  //     setSalesItems(updated)
  //     setProductNameError('')
  // }

  const handleProductName = (
    val: IndexDBProduct | null,
    id: number, // Change `id` to `number` to match the expected signature
    field: string,
    value: string,
  ) => {
    // Prefer exact batch match first, then fallback to product code only.
    // const stockObj =
    //   StockList.find(
    //     (pd) =>
    //       pd.productCode === val?.productCode &&
    //       pd.batchNumber === val?.batchNumber,
    //   ) ||
    //   StockList.find((pd) => pd.productCode === val?.productCode);

    // Extract product details from the `Product` object
    // let productName = val
    //   ? `${val.productName}-${val.productCode}-${val.sac}-${val.gst}`
    //   : "";
    let productName = val
      ? `${val.productName}`
      : "";

    let updated = [...salesItems];
    let ind = updated.findIndex((pi) => pi.id.toString() === id.toString());

    // Check if the product already exists in the sales items
    const existSalesItem = updated.findIndex(
      (pi) => pi.productName?.value === val?.id?.toString(),
    );

    if (existSalesItem > -1 && val !== null) {
      updated[ind].productName = { label: "", value: "" };
      let qty = parseInt(updated[existSalesItem]?.qty) + 1;

      updated[existSalesItem].qty = qty.toString();

      // Calculate GST value
      let gval =
        ((qty ? parseFloat(qty.toString()) : 0) *
          (updated[existSalesItem]?.rate
            ? parseFloat(updated[existSalesItem]?.rate!)
            : 0) -
          (updated[existSalesItem].disAmt
            ? parseFloat(updated[existSalesItem]?.disAmt!)
            : 0)) *
        ((updated[existSalesItem].gstPercent
          ? parseFloat(updated[existSalesItem]?.gstPercent!)
          : 0) /
          (100 +
            (updated[existSalesItem].gstPercent
              ? parseFloat(updated[existSalesItem]?.gstPercent!)
              : 0)));
      updated[ind].disAmt = "0"; // default no discount
      // const qty = parseFloat(updated[ind].qty || "0");
      const rate = parseFloat(updated[ind].rate || "0");
      const totalAfterDiscount = qty * rate; // disAmt = 0
      updated[ind].totalAmt = totalAfterDiscount.toFixed(2);
      const gstPercent = parseFloat(updated[ind].gstPercent || "0");
      const taxable =
        gstPercent >= 0
          ? totalAfterDiscount / (1 + gstPercent / 100)
          : totalAfterDiscount;
      updated[ind].gstPercent = gstPercent.toFixed(2);
      updated[ind].taxable = taxable.toFixed(2);
      updated[ind].gstValue = (totalAfterDiscount - taxable).toFixed(2);
      updated[existSalesItem].gstValue = gval?.toFixed(2);

      // Calculate taxable amount
      let tax =
        (qty ? parseFloat(qty.toString()) : 0) *
          (updated[existSalesItem].rate
            ? parseFloat(updated[existSalesItem].rate!)
            : 0) -
        (updated[existSalesItem].disAmt
          ? parseFloat(updated[existSalesItem].disAmt!)
          : 0) -
        gval;
      updated[existSalesItem].taxable = tax?.toFixed(2);

      // Calculate total amount
      let tot = gval + tax;
      // updated[existSalesItem].totalAmt = tot?.toFixed(2);
      updated[ind] = recalculateLineItem(updated[ind]);

      setSalesItems(updated);
      return;
    }

    if (ind > -1) {
      if (val === null) {
        // Clear the product details if `val` is null
        updated[ind] = {
          ...updated[ind],
          productName: null,
          productCode: "",
          modelNumber: "",
          mrp: "0",
          rate: "0",
          qty: "",
          disAmt: "0",
          pdPercent: 0,
          pdAmount: 0,
          totalAmt: "0.00",
        };
        setSalesItems(updated);
        setProductNameError("");
        return;
      }

      // Convert the `Product` object to a `Dropdown` object
      updated[ind].productName = {
        label: productName,
        value: val?.id ? val.id.toString():''
      };
      updated[ind].modelNumber = val?.modelNumber || "";
      // Update other fields
      updated[ind].productCode = val?.productCode
        ? val.productCode.toString()
        : "";
      updated[ind].productGroupCode = val?.productCode
        ? val.productCode.toString()
        : "";
      updated[ind].gstPercent = val?.gst ? val.gst.toString() : "";
      updated[ind].qty = "";
      updated[ind].mrp = val?.mrp?.toString() || "0";
      updated[ind].rate =
        val?.retailRate?.toString() || val?.mrp?.toString() || "0";
      updated[ind].hsn = val?.hsnCode ? val.hsnCode.toString() : "";
      updated[ind].batchNo = val?.batchNumber
        ? val.batchNumber.toString()
        : "";
      updated[ind].expiryDt =  null;
      updated[ind].igst = val?.igst
        ? val.igst.toString()
        : "";
      updated[ind].cgst = val?.cgst
        ? val.cgst.toString()
        : "";
      updated[ind].sgst = val?.sgst
        ? val.sgst.toString()
        : "";
      updated[ind].pwog = val?.pwoGst
        ? val.pwoGst.toString()
        : "";
      updated[ind].pwg = val?.pwGst ? val.pwGst.toString() : "";

      const qty = parseFloat(updated[ind].qty || "0");
      const rate = parseFloat(updated[ind].rate || "0");
      const gstPercent = parseFloat(updated[ind].gstPercent || "0");

      const taxable =
        rate && gstPercent >= 0
          ? (qty * rate) / (1 + gstPercent / 100)
          : qty * rate; // if no GST, taxable = MRP * qty

      updated[ind].taxable = taxable.toFixed(2);
      // Calculate unit profit
      let uprofit =
        (val?.wholesaleRate ? val.wholesaleRate : 0) -
        (val?.pwoGst ? val.pwoGst : 0);
      updated[ind].profitPercent = (
        ((uprofit ? uprofit : 0) /
          (val?.pwoGst ? val.pwoGst : 1)) *
        100
      ).toFixed(2);

      // Calculate GST value
      let gval =
        ((updated[ind].qty ? parseFloat(updated[ind].qty) : 0) *
          (updated[ind].rate ? parseFloat(updated[ind].rate!) : 0) -
          (updated[ind].disAmt ? parseFloat(updated[ind].disAmt!) : 0)) *
        ((val?.gst ? parseFloat(val.gst.toString()) : 0) /
          (100 + (val?.gst ? parseFloat(val.gst.toString()) : 0)));
      updated[ind].gstValue = gval?.toFixed(2);

      // Calculate taxable amount
      let tax =
        (updated[ind].qty ? parseFloat(updated[ind].qty) : 0) *
          (updated[ind].rate ? parseFloat(updated[ind].rate!) : 0) -
        (updated[ind].disAmt ? parseFloat(updated[ind].disAmt!) : 0) -
        gval;
      updated[ind].taxable = tax?.toFixed(2);

      // Calculate total amount
      // let tot = gval + tax;
      // updated[ind].totalAmt = tot?.toFixed(2);
      let mrpTotal =
        parseFloat(updated[ind].rate || "0") *
        parseFloat(updated[ind].qty || "0");
      updated[ind].totalAmt = mrpTotal.toFixed(2);

      // Calculate discount
      // let dis =
      //     (stockObj?.mrp ? stockObj.mrp : 0) -
      //     (stockObj?.lRate ? stockObj.lRate : 0);
      // updated[ind].disAmt = dis?.toFixed(2);
    }

    if (val === null) {
      updated[ind].qty = "";
    }

    setSalesItems(updated);
    setProductNameError("");
  };

  const handleProductCode = (value: string, id: string) => {
    let updated = [...salesItems];
    let ind = updated.findIndex((pi) => pi.id.toString() === id);
    if (ind > -1) {
      updated[ind].productCode = value;
    }
    setSalesItems(updated);
  };
   const handlemodelNumber = (value: string, id: string) => {
    let updated = [...salesItems];
    
    let ind = updated.findIndex((pi) => pi.id.toString() === id);
    if (ind > -1) {
      updated[ind].modelNumber= value;
    }
    setSalesItems(updated);
  };
  const handleGst = (value: string, id: string) => {
    let updated = [...salesItems];
    let ind = updated.findIndex((pi) => pi.id.toString() === id);
    if (ind > -1) {
      updated[ind].gstPercent = value;
    }
    setSalesItems(updated);
  };

  const handleQty = (value: string, id: string) => {
    // To reset redeem points
    value = value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) value = parts[0] + "." + parts[1];
    if (isCustPointsRedeemed) {
      let cObj = customerList?.find(
        (c) => c.accountNumber?.toString() === customer?.value?.toString(),
      );
      let cpoints = cObj?.score ? parseInt(cObj?.score) : 0;
      let amt = parseFloat(finalAmount) + cpoints;
      setFinalAmount(amt?.toString());
      setPreviousPoints(cpoints);
      setIsCustPointsRedeemed(false);
    }

    let updated = [...salesItems];
    let ind = updated.findIndex((pi) => pi.id.toString() === id);

    if (ind > -1) {
      updated[ind].qty = value;
      updated[ind] = recalculateLineItem(updated[ind]);
      setSalesItems(updated);
    }
  };

  const handleDiscountAmt = (value: string, id: string) => {
    const updated = [...salesItems];
    const ind = updated.findIndex((pi) => pi.id.toString() === id);
    if (ind > -1) {
      updated[ind].disAmt = value || "0";
      updated[ind] = recalculateLineItem(updated[ind]);
      setSalesItems(updated);
    }
  };

  const recalculateTotals = (items: SalesItem[]) => {
    const newGrandTotal = items.reduce((total, item) => {
      const itemTotal =
        parseFloat(item.rate || "0") * parseFloat(item.qty || "0");
      const discountAmount = item.pdAmount || 0; // Use the individual discount amount
      return total + (itemTotal - discountAmount);
    }, 0);

    // const newAmountPaid = newGrandTotal;

    // Update the state
    setNetAmt(newGrandTotal.toFixed(2));
    setGTotal(Math.round(newGrandTotal)?.toString());
    // setAmtPaid(newAmountPaid.toFixed(2));
  };

  const handlePDPercentChange = (value: string, id: string) => {
    const updated = [...salesItems];
    const index = updated.findIndex((item) => item.id.toString() === id);
    if (index > -1) {
      updated[index].pdPercent = parseFloat(parseFloat(value).toFixed(4));
      updated[index].pdAmount =
        (parseFloat(value) / 100) * parseFloat(updated[index].rate || "1");
    }
    setSalesItems(updated);
    recalculateTotals(updated);
  };

  const handlePDAmountChange = (value: string, id: string) => {
    const updated = [...salesItems];
    const index = updated.findIndex((item) => item.id.toString() === id);
    if (index > -1) {
      updated[index].pdAmount = parseFloat(parseFloat(value).toFixed(4));
      updated[index].pdPercent =
        (parseFloat(value) / parseFloat(updated[index].rate || "1")) * 100;
    }
    setSalesItems(updated);
    recalculateTotals(updated);
  };

  const applyGeneralDiscount = () => {
    const discountPercent = parseFloat(discountValue);
    const updated = salesItems.map((item) => {
      if (item.productName) {
        const pDiscountAmount =
          (discountPercent / 100) * parseFloat(item.rate || "0");
        return {
          ...item,
          pdPercent: discountPercent,
          pdAmount: parseFloat(pDiscountAmount.toFixed(4)), // Ensure type consistency
        };
      }
      return item;
    });

    setSalesItems(updated);
    recalculateTotals(updated);
  };

  useEffect(() => {
    applyGeneralDiscount();
  }, [discountValue]);

  const handleRate = (value: string, id: string) => {
    const updated = [...salesItems];
    const ind = updated.findIndex((pi) => pi.id.toString() === id);
    if (ind > -1) {
      updated[ind].rate = value;
      updated[ind] = recalculateLineItem(updated[ind]);
      setSalesItems(updated);
    }
  };

  const handleGSTValue = (value: string, id: string) => {
    let updated = [...salesItems];
    let ind = updated.findIndex((pi) => pi.id.toString() === id);
    if (ind > -1) {
      updated[ind].gstValue = value;
    }
    setSalesItems(updated);
  };

  const handleTaxable = (value: string, id: string) => {
    let updated = [...salesItems];
    let ind = updated.findIndex((pi) => pi.id.toString() === id);
    if (ind > -1) {
      updated[ind].taxable = value;
    }
    setSalesItems(updated);
  };

  const handleTotAmt = (value: string, id: string) => {
    let updated = [...salesItems];
    let ind = updated.findIndex((pi) => pi.id.toString() === id);
    if (ind > -1) {
      updated[ind].totalAmt = value;
    }
    // if (ind > -1) {
    //     updated[ind].qty = value;
    //     updated[ind] = recalculateLineItem(updated[ind]);
    //     setSalesItems(updated);
    // }
    setSalesItems(updated);
  };

  //Bill side
  const handlePaymentMode: ChangeEventHandler<HTMLInputElement> = (e) => {
    setPaymentMode(e.target.value as SalesPaymentModeTypes);
    setPaymentModeError("");
  };

  const handleBank: ChangeEventHandler<HTMLInputElement> = (e) => {
    setBankNumber(e.target.value);
    if (e.target.value) {
      let obj = AccountLists.find(
        (a) => a.accountNumber?.toString() === e.target.value.toString(),
      );
      setBankName(obj?.accountName ? obj?.accountName : "");
    }
  };
  const handleAmtPaid: ChangeEventHandler<HTMLInputElement> = (e) => {
    setAmtPaid(e.target.value);
    setAmtPaidError("");
  };
  const handleBalance: ChangeEventHandler<HTMLInputElement> = (e) => {
    setBalance(e.target.value);
    setBalanceError("");
  };
  const handleDisAmt: ChangeEventHandler<HTMLInputElement> = (e) => {
    setDisAmt(e.target.value);
    setDisAmtError("");
  };

  // const handleCustomerContact: ChangeEventHandler<HTMLInputElement> = (e) => {
  //     setCustomer(e.target.value)
  //     // setCustomerError('')
  // }
  const handleTotalAmt: ChangeEventHandler<HTMLInputElement> = (e) => {
    setTotAmt(e.target.value);
    setTotAmtError("");
  };
  const handleBillAmt: ChangeEventHandler<HTMLInputElement> = (e) => {
    setBillAmount(e.target.value);
    setBillAmountError("");
  };
  const handleTotalItems: ChangeEventHandler<HTMLInputElement> = (e) => {
    setTotalItems(e.target.value);
    setTotalItemsError("");
  };
  const handlePackNo: ChangeEventHandler<HTMLInputElement> = (e) => {
    setPackNo(e.target.value);
    setPackNoError("");
  };
  const handleNetTaxable: ChangeEventHandler<HTMLInputElement> = (e) => {
    setNetTaxable(e.target.value);
    setNetTaxableError("");
  };
  const handleNetGST: ChangeEventHandler<HTMLInputElement> = (e) => {
    setNetGSTValue(e.target.value);
    setNetGSTValueError("");
  };

  const handleYourSavings: ChangeEventHandler<HTMLInputElement> = (e) => {
    setYourSavings(e.target.value);
    setYourSavingsError("");
  };
  const handleNetIgst: ChangeEventHandler<HTMLInputElement> = (e) => {
    setNetIgst(e.target.value);
    setNetIgstError("");
  };
  const handleNetCgst: ChangeEventHandler<HTMLInputElement> = (e) => {
    setNetCgst(e.target.value);
    setNetCgstError("");
  };
  const handleNetSgst: ChangeEventHandler<HTMLInputElement> = (e) => {
    setNetSgst(e.target.value);
    setNetSgstError("");
  };
  const handleTotalQty: ChangeEventHandler<HTMLInputElement> = (e) => {
    setTotalQty(e.target.value);
    setTotalQtyError("");
  };
  const handleBillNetAmt: ChangeEventHandler<HTMLInputElement> = (e) => {
    setBillNetAmt(e.target.value);
    setBillNetAmtError("");
  };
  const handleNetAmt: ChangeEventHandler<HTMLInputElement> = (e) => {
    setNetAmt(e.target.value);
    setNetAmtError("");
  };
  const handleRoundOff: ChangeEventHandler<HTMLInputElement> = (e) => {
    setRoundOff(e.target.value);
    setRoundOffError("");
  };

  const handleAdd: ChangeEventHandler<HTMLInputElement> = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts.length > 2) value = parts[0] + "." + parts[1];
    setAdd(value);
    setAddError("");
  };
  const handleLess: ChangeEventHandler<HTMLInputElement> = (e) => {
    setLess(e.target.value);
    setLessError("");
  };
  const handleGrandTotal: ChangeEventHandler<HTMLInputElement> = (e) => {
    setGTotal(e.target.value);
    setGTotalError("");
  };
  const handleTenderedAmt: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setTenderedAmt(value.toString());

    // Only auto-fill cash if no other payments are made yet
    if (!card1 && !card2 && !onlinePayment1 && !onlinePayment2) {
      const totalBalNum = parseFloat(totalBal.toString()) || 0;
      const cashToSet = Math.min(value, totalBalNum);
      setCash(cashToSet.toString());
    }
    if (value !== 0) {
      setCash(gtotal.toString());
      // setCash(value?.toString());
    }
  };
  const handleTenderedBal: ChangeEventHandler<HTMLInputElement> = (e) => {
    setTenderedBal(e.target.value);
    setTenderedBalError("");
  };

  const handleTenderedReturn: ChangeEventHandler<HTMLInputElement> = (e) => {
    setTenderedReturn(e.target.value);
    setTenderedReturnError("");
  };

  const handleView = () => {
    setShowSalesView(true);
  };

  const handleModalClose = () => {
    setAreaModal(false);
    setPlaceModal(false);
    setDistrictModal(false);
    setStateModal(false);
    setCountryModal(false);
    setRouteModal(false);
  };

  const handleBranch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setBranchId(e.target.value);
    setBranchIdError("");
  };

  const handleTag: ChangeEventHandler<HTMLInputElement> = (e) => {
    setNameTag(e.target.value);
    setNameTagError("");
  };

  const handleAccountName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setAccountName(e.target.value);
    setAccountNameError("");
  };

  const handleAddress1: ChangeEventHandler<HTMLInputElement> = (e) => {
    setAddress1(e.target.value);
    setAddress1Error("");
  };

  const handleAddress2: ChangeEventHandler<HTMLInputElement> = (e) => {
    setAddress2(e.target.value);
    setAddress2Error("");
  };

  const handleArea: ChangeEventHandler<HTMLInputElement> = (e) => {
    setArea(e.target.value);
    setAreaError("");
  };

  const handlePlace: ChangeEventHandler<HTMLInputElement> = (e) => {
    setPlace(e.target.value);
    setPlaceError("");
  };

  const handleDistrict: ChangeEventHandler<HTMLInputElement> = (e) => {
    setDistrict(e.target.value);
    setDistrictError("");
  };

  const handlePincode: ChangeEventHandler<HTMLInputElement> = (e) => {
    setPincode(e.target.value);
    setPincodeError("");
  };

  const handleState: ChangeEventHandler<HTMLInputElement> = (e) => {
    setState(e.target.value);
    setStateError("");
  };

  const handleCountry: ChangeEventHandler<HTMLInputElement> = (e) => {
    setCountry(e.target.value);
    setCountryError("");
  };

  const handleRoute: ChangeEventHandler<HTMLInputElement> = (e) => {
    setRoute(e.target.value);
    setRouteError("");
  };

  const handleContactNumber1: ChangeEventHandler<HTMLInputElement> = (e) => {
    const value = e.target.value;
    setContactNumber1(value);
    setContactNumber1Error("");
  };

  const handleContactNumber2: ChangeEventHandler<HTMLInputElement> = (e) => {
    setContactNumber2(e.target.value);
    setContactNumber2Error("");
  };

  const handleAadharNumber: ChangeEventHandler<HTMLInputElement> = (e) => {
    setAadharNumber(e.target.value);
    setAadharNumberError("");
  };
  const handlegstNumber: ChangeEventHandler<HTMLInputElement> = (e) => {
    setGstNumber(e.target.value);
    setGstNumberError("");
  };

  const handleHiddenGst: ChangeEventHandler<HTMLInputElement> = (e) => {
    setHiddenGst(!hiddenGst);
  };

  const handleFormClose = () => {
    // setShowSalesForm(false)
    setShowSalesView(false);
    setModal(false);
    setShowTenderModal(false);
  };

  const handleClose = () => {
    setConfirmModal(false);
    // setOptions(''); // reset selection
    setPrintObjectState(null);
  };

  const handleAreaName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setAreaName(e.target.value);
  };
  const handlePlaceName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setPlaceName(e.target.value);
  };

  const handleRouteName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setRouteName(e.target.value);
  };
  const handleDistrictName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setDistrictName(e.target.value);
  };
  const handleStateName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setStateName(e.target.value);
  };
  const handleCountryName: ChangeEventHandler<HTMLInputElement> = (e) => {
    setCountryName(e.target.value);
  };

  const generateBillPdf = (printObj: any) => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // const logoUrl = clients[0]?.shopPhoto;
    // const marginX = 10;
    let y = 5; // Start higher to save space

    // === Business Header ===
    // if (logoUrl) {
    //     doc.addImage(logoUrl, "JPEG", marginX, y, 20, 15); // Smaller image
    // }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(clients[0]?.shopFullName || "", 15, y + 2.5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      `${clients[0]?.address1 || ""}, ${clients[0]?.address2 || ""}`,
      15,
      y + 6,
    );
    doc.text(
      `${clients[0]?.district || ""} - ${clients[0]?.pincode || ""}`,
      15,
      y + 9.5,
    );
    doc.text("Cell: " + (clients[0]?.contactNo1 || ""), 15, y + 13);
    doc.text("GST No: " + (clients[0]?.gstNo || ""), 15, y + 16.5);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("SALES TAX INVOICE", 87, y + 9);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const img = document.getElementById("phonepe") as HTMLImageElement | null;
    // if (logoUrl) {
    if (img instanceof HTMLImageElement) {
      doc.addImage(img, "JPEG", 180, y, 15, 15); // Smaller image
    }
    doc.text("Gpay No: " + (clients[0]?.contactNo2 || ""), 166.5, y + 17.5);
    // }
    doc.line(15, y + 18.5, 195, y + 18.5); // Thinner top section

    // === Customer & Invoice Details ===
    y = y + 15; // Reduced spacing before next block

    // Customer block
    doc.setFont("helvetica", "normal");
    doc.text(`NAME: ${printObj.customer.name || ""}`, 15, y + 6);
    doc.text(`ADDRESS: ${printObj.customer.place} `, 15, y + 9);
    doc.text(`CELL: ${printObj.customer.conNo || ""}`, 15, y + 12);
    doc.text(`GSTIN: ${printObj.customer.gstNo || "NIL"}`, 15, y + 15);

    // Invoice block
    doc.setFont("helvetica", "bold");
    // doc.text("Invoice Details:", 120, y);
    doc.setFont("helvetica", "normal");
    doc.text(`Invoice No: ${printObj.billnumber}`, 156.5, y + 6);
    doc.text(`Date: ${printObj.date} ${printObj.time}`, 156.5, y + 9);
    doc.text(`Vehicle No: ${printObj.customer.conNo2}`, 156.5, y + 12);
    doc.text("Payment: CASH SALES", 156.5, y + 15);

    doc.line(15, y + 16, 195, y + 16);

    const tableY = y + 16; // 27.5

    const tableColumn = [
      "S.No",
      "Particulars",
      "HSN",
      "Qty",
      "Rate",
      "GST%",
      "Total",
    ];
    const tableRows =
      printObj?.productitems?.map((item: any, i: number) => [
        i + 1,
        item.name,
        item.hsn || "-",
        item.qty,
        item.rate,
        item.gst,
        // item.taxable,
        item.total,
      ]) || [];

    // === Product Table ===
    autoTable(doc, {
      startY: tableY,
      head: [tableColumn],
      body: tableRows,
      theme: "plain", // No grid lines by default
      styles: {
        fontSize: 8,
        cellPadding: 0.3,
        valign: "middle",
        lineWidth: 0, // Removes all body lines
      },
      headStyles: {
        textColor: 0,
        fontStyle: "bold",
        halign: "left",
      },
      columnStyles: {
        0: { halign: "center"},
        1: { halign: "left"},
        2: { halign: "left"},
        3: { halign: "center" },
        4: { halign: "right" },
        5: { halign: "center" },
        6: { halign: "right" },
        7: { halign: "left" },
      },
      margin: { left: 15, right: 15 },
      didParseCell: function (data) {
        if (
          data.section === "head" &&
          (data.column.index === 4 ||
            data.column.index === 6 ||
            data.column.index === 7)
        ) {
          data.cell.styles.halign = "right";
        }
        if (
          data.section === "head" &&
          (data.column.index === 3 || data.column.index === 5)
        ) {
          data.cell.styles.halign = "center";
        }
      },
      didDrawPage: (data) => {
        // draw a closing line after table body (end of table)
        if ((doc as any).lastAutoTable) {
          const table = (doc as any).lastAutoTable;
          const endY = table.finalY;
          doc.setDrawColor(0);
          doc.setLineWidth(0.2);
          doc.line(15, endY, 195, endY); // closing line below last row
        }
      },

      didDrawCell: (data) => {
        // Draw a line only under the header row
        if (data.section === "head" && data.row.index === 0) {
          const { x, y, width, height } = data.cell;
          data.doc.setDrawColor(0);
          data.doc.setLineWidth(0.2);
          data.doc.line(x, y + height, x + width, y + height);
        }
      },
    });

    // No extra space below product table
    let finalY = (doc as any).lastAutoTable.finalY;

    const TablelineY = Math.max(
      (doc as any).lastAutoTable.finalY,
      (doc as any).lastAutoTable.finalY,
    );
    doc.line(15, TablelineY, 195, TablelineY);

    // X positions
    let leftTableX = 14; // GST table (left)
    let rightTableX = 110; // Amount details (right)

    const commonY = finalY + 2.0;

    // GST Table (Left)
    // GST Table (Left)
    autoTable(doc, {
      startY: commonY + 1,
      margin: { left: leftTableX +5, top: 0, bottom: 0 },
      head: [["Taxable", "CGST", "SGST" ]],
      // head: [["Taxable", "CGST", "SGST", "Old Balance"]],
      body: [
        [
          parseFloat(printObj.taxable || "0").toFixed(2),
          printObj.cgst || "0",
          printObj.sgst || "0",
          printObj.previousBal + ".00" || "0",
        ],
      ],
      theme: "plain",
      styles: { fontSize: 8, halign: "center", cellPadding: 0.3 },
      tableLineWidth: 0,
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
      },
    });

    autoTable(doc, {
      startY: commonY - 1, // same Y as GST table for perfect horizontal alignment
      margin: { left: rightTableX + 20, top: 0, bottom: 0 },
      body: [
        ["Bill Amount", ":", parseFloat(printObj.finaltotal).toFixed(2)],
        ["Amount Paid", ":", parseFloat(printObj.finaltotal).toFixed(2)],
        ["Balance", ":", '0.00'],

        // old
        // ["Bill Amount", ":", printObj.finaltotal],
        // ["Amount Paid", ":", printObj.paymentamount],
        // ["Balance", ":", parseFloat(printObj.credit).toFixed(2)],
      ],
      theme: "plain",
      styles: { fontSize: 8, cellPadding: 0.3 },
      tableLineWidth: 0,
      columnStyles: {
        0: { fontStyle: "bold", halign: "right", cellWidth: 35 },
        1: { halign: "right", cellWidth: 5 },
        2: { halign: "right", cellWidth: 25 },
      },
    });

    // Horizontal line right below both tables
    const lineY = Math.max(
      (doc as any).lastAutoTable.finalY,
      (doc as any).lastAutoTable.finalY,
    );
    doc.line(15, lineY, 195, lineY);

    // *Footer Text*
    doc.text(
      "Goods Once Sold Cannot be taken back. Thanks Come Again.",
      67,
      finalY + 20,
    );

    return doc.output("blob");
  };

  const recalculateLineItem = (item: SalesItem): SalesItem => {
    const qty = parseFloat(item.qty || "0");
    const rate = parseFloat(item.rate || "0");
    const discountPerUnit = parseFloat(item.disAmt || "0"); // ← treat as per-unit
    const gstPercent = parseFloat(item.gstPercent || "0");

    // ✅ CORRECT: Apply discount per unit
    const totalAfterDiscount = (rate - discountPerUnit) * qty;

    // Ensure non-negative total
    const finalTotal = Math.max(0, totalAfterDiscount);

    const taxable =
      gstPercent >= 0 ? finalTotal / (1 + gstPercent / 100) : finalTotal;

    const gstValue = finalTotal - taxable;

    return {
      ...item,
      taxable: taxable.toFixed(2),
      gstValue: gstValue.toFixed(2),
      totalAmt: finalTotal.toFixed(2),
    };
  };

  const queueThermalReceiptPrint = (printObj: SalesPrintObject) => {
    const branchThermalId = Number(AuthUser?.branchId) || 0;
    void getSettings().then((settings) =>
      postThermalSalesReceiptIfNeeded({
        printObj,
        client: clients[0],
        settings,
        branchId: branchThermalId,
        paymentLabel: thermalPaymentLabel(paymentMode),
        apiBaseUrl: apiUrl || "",
      }).then((r) => {
        if (r.attempted && !r.ok) {
          toast(r.error || "Thermal printer request failed", ToastTypes.ERROR);
        }
      })
    );
  };

  const handleSave = () => {
  let error = false;

  // Filter valid sales items
  const data = salesItems?.filter((f) => {
    return (
      f.productName?.label !== "" &&
      f.productName?.label !== undefined &&
      f.productName?.value !== "" &&
      f.productName?.value !== undefined
    );
  });

  // Validate items
  if (data?.length > 0) {
    data?.forEach((d) => {
      if (!d.productName?.value) {
        toast(`In Row ${d.id}, Product required`, ToastTypes.ERROR);
        error = true;
      }
      if (!d.productCode) {
        toast(`In Row ${d.id}, Product Code required`, ToastTypes.ERROR);
        error = true;
      }
      if (!d.gstPercent) {
        toast(`In Row ${d.id}, GST percent required`, ToastTypes.ERROR);
        error = true;
      }
      if (!d.qty) {
        toast(`In Row ${d.id}, Quantity required`, ToastTypes.ERROR);
        error = true;
      }
      if (!d.gstValue) {
        toast(`In Row ${d.id}, GST Value required`, ToastTypes.ERROR);
        error = true;
      }
      if (!d.rate) {
        toast(`In Row ${d.id}, Rate required`, ToastTypes.ERROR);
        error = true;
      }
      if (!d.taxable) {
        toast(`In Row ${d.id}, Taxable required`, ToastTypes.ERROR);
        error = true;
      }
      if (!d.totalAmt) {
        toast(`In Row ${d.id}, Total Amt required`, ToastTypes.ERROR);
        error = true;
      }
    });
  } else {
    toast("Data Required", ToastTypes.ERROR);
    error = true;
  }

  // Validate header fields
  if (!financialYear) { toast("Financial Year required", ToastTypes.ERROR); error = true; }
  if (!invoiceType) { toast("Invoice type required", ToastTypes.ERROR); error = true; }
  if (!invoiceTitle) { toast("Invoice title required", ToastTypes.ERROR); error = true; }
  if (!billNo) { toast("Bill No required", ToastTypes.ERROR); error = true; }
  if (!customer?.value) { toast("Customer required", ToastTypes.ERROR); error = true; }
  // if (!customerName) { toast("Customer name required", ToastTypes.ERROR); error = true; }
  if (!billDate) { toast("Bill date required", ToastTypes.ERROR); error = true; }
  if (!gtotal) { toast("Grand total required", ToastTypes.ERROR); error = true; }
  if (!Object.values(SalesPaymentModesDropDown).includes(paymentMode)) {
    toast("Payment Mode must be BANK/CASH/CREDIT", ToastTypes.ERROR);
    error = true;
  }
  if (!totalQty) { toast("Total Qty required", ToastTypes.ERROR); error = true; }
  if (!totalItems) { toast("Total Items required", ToastTypes.ERROR); error = true; }

  if (!error) {
    // ===== CALCULATIONS =====
    const netTaxable = data.reduce((sum, cur) => sum + parseFloat(cur.taxable || "0"), 0);
    const netPercent = data.reduce((sum, cur) => sum + parseFloat(cur.profitPercent || "0"), 0);

    // GST slab calculations (with NaN protection)
    const calcSlab = (gst: string) => {
      const slabData = data.filter((a) => a?.gstPercent?.toString() === gst);
      const value = slabData.reduce((sum, cur) => 
        sum + 
        parseFloat(cur.igst || "0") + 
        parseFloat(cur.cgst || "0") + 
        parseFloat(cur.sgst || "0"), 0);
      const taxable = slabData.reduce((sum, cur) => sum + parseFloat(cur.taxable || "0"), 0);
      return { value: value.toString(), taxable: taxable.toString() };
    };
    const { value: AValue, taxable: ATaxable } = calcSlab("0");
    const { value: BValue, taxable: BTaxable } = calcSlab("5");
    const { value: CValue, taxable: CTaxable } = calcSlab("12");
    const { value: DValue, taxable: DTaxable } = calcSlab("18"); // ✅ Fixed NaN
    const { value: EValue, taxable: ETaxable } = calcSlab("28");

    const invoiceDateObj = billDate || new Date(); 
    const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
    const dayName = days[invoiceDateObj.getDay()];

    // ===== PAYMENT LOGIC (LOCAL VARIABLES) =====
    // Use LOCAL variables to avoid async state issues
    let pCash = cash || "0";
    let pCard1 = card1 || "0";
    let pCard2 = card2 || "0";
    let pOnline1 = onlinePayment1 || "0";
    let pOnline2 = onlinePayment2 || "0";
    let pTenderAmt = tenderedAmt || "0";
    let pTenderReturn = tenderedReturn || "0";
    let pCredit = balance || "0";
    let pAmtPaid = amtPaid || "0";
    let pRemark = remark || "";
    let pSplit = split || "No";

    const totalEntered = 
      parseFloat(pCash) + parseFloat(pCard1) + parseFloat(pCard2) + 
      parseFloat(pOnline1) + parseFloat(pOnline2);
    const totalBalNum = parseFloat(gtotal) + parseFloat(previousBal || "0");

    // ✅ AUTO-FILL: If modal skipped (no payments entered), default to FULL CASH
    if (totalEntered === 0 && paymentMethod !== "credit") {
      pCash = gtotal;
      pAmtPaid = gtotal;
      pCredit = "0";
      pTenderAmt = gtotal;
      pTenderReturn = "0";
      pRemark = "C";
      pSplit = "No";
    } 
    // Handle Credit mode
    else if (paymentMethod === "credit") {
      pCash = "0";
      pCard1 = "0";
      pCard2 = "0";
      pOnline1 = "0";
      pOnline2 = "0";
      pAmtPaid = "0";
      pCredit = totalBalNum.toString();
      pTenderAmt = "0";
      pTenderReturn = "0";
      pRemark = "Credit";
      pSplit = "No";
    }

    // ===== BUILD TRANSACTIONS =====
    const transactions: any[] = [];
    
    // Cash transaction (only if cash was actually used)
    if (parseFloat(pCash) > 0) {
      const cashPaid = parseFloat(pTenderReturn) > 0 
        ? parseFloat(pTenderAmt) - parseFloat(pTenderReturn) 
        : parseFloat(pCash);
      transactions.push({
        paymentMode: "Cash",
        particulars: "Cash",
        amtPaid: cashPaid,
      });
    }
    // Bank/Card transactions
    if (parseFloat(pCard1) > 0) transactions.push({ paymentMode: "Bank", particulars: "Card1", amtPaid: parseFloat(pCard1) });
    if (parseFloat(pCard2) > 0) transactions.push({ paymentMode: "Bank", particulars: "Card2", amtPaid: parseFloat(pCard2) });
    if (parseFloat(pOnline1) > 0) transactions.push({ paymentMode: "Bank", particulars: "Online Payment 1", amtPaid: parseFloat(pOnline1) });
    if (parseFloat(pOnline2) > 0) transactions.push({ paymentMode: "Bank", particulars: "Online Payment 2", amtPaid: parseFloat(pOnline2) });
    // Credit transaction
    if (paymentMethod === "credit" && parseFloat(pCredit) > 0) {
      transactions.push({ paymentMode: "Credit", particulars: "Credit", amtPaid: 0 });
    }

    // ===== BUILD PAYLOAD =====
    let addData: SalesType = {
      financeYear: financialYear,
      branchId: AuthUser?.branchId || 0,
      invoiceDate: getDate(billDate),
      invoiceType: invoiceType,
      invoiceTitle: invoiceTitle,
      invoiceDay: dayName,
      invoiceWeekNumber: 1,
      invoiceWeekDay: billDate.getDay(),
      invoiceMonth: billDate.getMonth() + 1,
      invoiceYear: billDate.getFullYear(),
      invoiceNumber: billNo || "0",
      packNo: packNo,
      salesEntryNumber: parseInt(salesEntryNo),
      repId: parseInt(refBy),
      customerId: customer?.value || 0,
      pointsEarned: pointsEarned.toString(),
      systemNo: sysNo,
      placeOfSupply: placeOfSupply,
      paymentMode: paymentMode,
      customerName: customerName || "",
      bankName: bankName,
      bankNumber: bankNumber ? parseInt(bankNumber) : 0,
      billAmount: netAmt,
      disPercent: disPercent,
      disAmount: disAmt,
      totalAmount: TotalBillAmt,
      totalDiscount: TotalDisAmt,
      roundOff: roundOff,
      prevBal:
        previousBal != null && previousBal !== "" ? String(previousBal) : "0",
      totalBal: totalBalNum.toString(),
      // ✅ USE LOCAL VARIABLES (not state)
      cash: pCash,
      card1: pCard1,
      card2: pCard2,
      onlinePayment1: pOnline1,
      onlinePayment2: pOnline2,
      credit: pCredit,
      ledgerBal: ledgerBal,
      bankReceived: bankRecevied,
      add: add,
      less: less,
      grandTotal: gtotal,
      amtInWords: amtInWords,
      particulars: "sales-" + packNo,
      amtPaid: pAmtPaid,
      deliveryMode: deliveryMode,
      deliveryDetails: deliveryDetails,
      remarks: pRemark,
      tenderAmt: pTenderAmt,
      tenderReturn: pTenderReturn,
      netTaxable: netTaxable.toString(),
      netIgst: "0",
      netSgst: netSgst,
      netCgst: netCgst,
      netProfit: totalProfit,
      netProfitPercent: netPercent.toString(),
      AValue,
      ATaxable,
      BValue,
      BTaxable,
      CValue,
      CTaxable,
      DValue,
      DTaxable,
      EValue,
      ETaxable,
      isPointsRedeemed: isCustPointsRedeemed,
      split: pSplit,
      transactions: transactions, // ✅ Now properly populated
      salesItems: data.map((dd) => {
        const mrpTotal = parseFloat(dd.rate || "0") * parseFloat(dd.qty || "0");
        const tprofit =
          parseFloat(dd.qty) * parseFloat(dd.rate!) -
          parseFloat(dd.qty) * parseFloat(dd.pwog!);
        const unitProfit =
          (dd.rate ? parseFloat(dd.rate) : 0) -
          (dd.pwog ? parseFloat(dd.pwog) : 0);
        const profitPercent =
          ((unitProfit || 0) / (dd.pwog ? parseFloat(dd.pwog) : 1)) * 100;

        return {
          entryNumber: parseInt(salesEntryNo),
          productId: dd.productName?.value ? parseInt(dd.productName.value) : 0,
          productCode: dd.productCode,
          productGroupCode: dd.productGroupCode,
          batchNumber: dd.batchNo ? parseInt(dd.batchNo) : 0,
          expiryDate: dd.expiryDt ? getDate(dd.expiryDt.toString()) : "",
          quantity: dd.qty,
          freeQuantity: "0",
          totalQuantity: dd.qty,
          priceWogst: parseFloat(dd.pwog || "0"),
          priceGst: parseFloat(dd.pwg || "0"),
          wRate: parseFloat(dd.rate || "0"),
          mrp: parseFloat(dd.mrp || "0"),
          gstValue: parseFloat(dd.gstValue || "0"),
          sDiscount: parseFloat(dd.disAmt || "0"),
          pDiscountpercent: dd.pdPercent ?? 0,
          pDiscountamount: dd.pdAmount ?? 0,
          cDiscountpercent: 0,
          cDiscountamount: 0,
          taxableAmount: parseFloat(dd.taxable || "0"),
          igstAmount: dd.igst ? parseFloat(dd.igst) : 0,
          cgstAmount: dd.cgst ? parseFloat(dd.cgst) : 0,
          sgstAmount: dd.sgst ? parseFloat(dd.sgst) : 0,
          totalAmount: mrpTotal,
          unitCost: unitProfit,
          profitAmount: tprofit,
          profitPercent: parseFloat(profitPercent.toFixed(2)),
          modelNumber: dd.modelNumber || "",
        };
      }),
    };

    // ===== PRINT OBJECT =====
    const printObj = {
      kotnumber: 1,
      invoiceType: addData.invoiceType,
      taxable: netTaxable,
      previousBal: addData.prevBal,
      credit: addData.credit,
      cgst: addData.netCgst,
      sgst: addData.netSgst,
      billnumber: addData.invoiceNumber,
      tablename: "table1",
      customer: customer,
      cashierid: AuthUser?.userId,
      systemid: addData.systemNo,
      placeOfSupply: resolvePlaceOfSupply(placeOfSupply),
      date: LocalFormatDate(getDate(billDate)),
      printername: "",
      time: new Date().toLocaleTimeString("en-US", {
        timeZone: "Asia/Kolkata", hour12: true,
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      }),
      productitems: salesItems
        .filter((item) => item.productName?.value)
        .map((item) => {
          const qty = parseFloat(item.qty || "0");
          const rate = parseFloat(item.rate || "0");
          const discountPerUnit = parseFloat(item.disAmt || "0");
          const totalAfterDiscount = Math.max(0, (rate - discountPerUnit) * qty);
          const gstPercent = parseFloat(item.gstPercent || "0");
          const taxable = gstPercent >= 0 ? totalAfterDiscount / (1 + gstPercent / 100) : totalAfterDiscount;
          return {
            name: item.productName?.label || "",
            qty: item.qty,
            rate: rate.toFixed(2),
            disc: discountPerUnit.toFixed(2),
            total: totalAfterDiscount.toFixed(2),
            taxable: taxable.toFixed(2),
            gst: item.gstPercent,
            hsn: item.hsn,
          };
        }),
      empname: AuthUser?.name,
      grandtotal: finalAmount,
      discount: [], gst: [], oc: [],
      finaltotal: parseFloat(addData.grandTotal || "0").toFixed(2),
      totaldisc: parseFloat(addData.disAmount || "0").toFixed(2),
      totalqty: parseFloat(totalQty),
      paymentamount: parseFloat(pAmtPaid).toFixed(2),
      tender: parseFloat(pTenderAmt).toFixed(2),
      balance: parseFloat(pTenderReturn).toFixed(2),
      pointsearned: pointsEarned > 0 ? pointsEarned : 0,
      totalpoints: totalpoints > 0 ? totalpoints : 0,
    };

    // ===== API CALL =====
    setShowOverlayLoader();
    const apiCall = formType === FormTypeList.ADD 
      ? dispatch(addSalesAction(addData))
      : dispatch(updateSalesAction(addData, id!));

    apiCall
      .then((text) => {
        toast(text, ToastTypes.SUCCESS);
        setPrintObjectState(printObj);
        setConfirmModal(false);
        queueThermalReceiptPrint(printObj as SalesPrintObject);
        refreshProductsFromAPI();
        dispatch(fetchSalesAction()); 
        resetStates();
      })
      .catch((text) => toast(text, ToastTypes.ERROR))
      .finally(() => {
        setHideOverlayLoader();
        fetchCustomer();
      });
  }
};

  const handleOptionSubmit = () => {
    const printObj = printObjectState;
    if (!printObj) {
      toast("Nothing to print");
      setConfirmModal(false);
      return;
    }

    const branchThermalId = Number(AuthUser?.branchId) || 0;
    void getSettings().then((settings) => {
      const isThermal = isThermalPrinterFromSettings(settings, branchThermalId);

      void postThermalSalesReceiptIfNeeded({
        printObj,
        client: clients[0],
        settings,
        branchId: branchThermalId,
        paymentLabel: thermalPaymentLabel(paymentMode),
        apiBaseUrl: apiUrl || "",
      }).then((r) => {
        if (r.attempted && !r.ok) {
          toast(r.error || "Thermal printer request failed", ToastTypes.ERROR);
        }
      });

      if (!isThermal && options === "print") {
        // Only open print preview (no download)
        const pdfBlob = generateBillPdf(printObj);
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const printWindow = window.open(pdfUrl, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        } else {
          toast("Please allow popups to print the invoice.");
        }
        fetch("http://localhost:8080/Print/billprint.jsp", {
          method: "POST",
          body: JSON.stringify(printObj),
          mode: "no-cors",
          headers: { "Content-Type": "application/json" },
        }).catch((err) => console.log(err.message));
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 3000);
      } else if (!isThermal && options === "download") {
        const pdfBlob = generateBillPdf(printObj);
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `Sales Invoice-${printObj.date} ${printObj.time}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        const printWindow = window.open(pdfUrl, "_blank");
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        } else {
          toast("PDF downloaded. Please allow popups to open print preview.");
        }

        setTimeout(() => URL.revokeObjectURL(pdfUrl), 3000);

        fetch("http://localhost:8080/Print/billprint.jsp", {
          method: "POST",
          body: JSON.stringify(printObj),
          mode: "no-cors",
          headers: { "Content-Type": "application/json" },
        }).catch((err) => console.log(err.message));
      }
    });

    setConfirmModal(false);
    // setOptions('');
    setPrintObjectState(null);

    // After choosing print/download, reset states and refresh lists
    resetStates();
    dispatch(fetchSalesAction());
    fetchRecentSalesId();
  };
  useEffect(() => {
    if (confirmModal) {
      setOptions("print");
    }
  }, [confirmModal]);

  const fetchRecentSalesId = async () => {
    try {
      const response = await api().get(`/sales/recent/`);

      if (response.status === 200 && response.data.data) {
        const { recentId1, recentId2 } = response.data.data;

        // Determine the prefix based on invoiceType
        const prefix = `TAP${
          invoiceType === SALES_TYPE_DROPS[0].value ? "TI" : "ES"
        }`;
        const lastInvoiceNumber =
          invoiceType === SALES_TYPE_DROPS[0].value ? recentId1 : recentId2;

        if (lastInvoiceNumber) {
          const numericPart = lastInvoiceNumber.replace(prefix, ""); // Extract numeric part
          const parsedNumber = parseInt(numericPart, 10); // Convert to integer

          if (isNaN(parsedNumber)) {
            console.error("Failed to parse invoice number:", lastInvoiceNumber);
            setBillNo(`${prefix}10001`);
          } else {
            const newNumericPart = parsedNumber + 1; // Increment numeric part
            const formattedNumber = `${prefix}${newNumericPart
              .toString()
              .padStart(5, "0")}`; // Format to 5 digits
            if (formType === FormTypeList.ADD) setBillNo(formattedNumber);
          }
        } else {
          setBillNo(`${prefix}10001`); // Default invoice number if none exists
        }
      } else {
        setBillNo(
          `TAP${invoiceType === SALES_TYPE_DROPS[0].value ? "TI" : "ES"}10001`,
        );
      }
    } catch (error) {
      console.error("Error fetching recent sales ID:", error);
    }
  };

  const redeemCustomerPoints = () => {
    if (previousPoints > 0) {
      let amt = parseFloat(finalAmount) - previousPoints;
      setFinalAmount(amt?.toString());
      setPreviousPoints(0);
      setIsCustPointsRedeemed(true);
    }
  };

  useEffect(() => {
    fetchRecentSalesId();
  }, [formType, invoiceType]);

  useEffect(() => {
    // PURE MRP-BASED GRAND TOTAL
    const mrpTotal = salesItems
      .filter((f) => f.productName?.value)
      .reduce((sum, item) => {
        return sum + parseFloat(item.mrp || "0") * parseFloat(item.qty || "0");
      }, 0);
    const rounded = Math.round(mrpTotal);
    setGTotal(rounded.toString());
    setFinalAmount(rounded.toString());
  }, [salesItems]);

  useEffect(() => {
    // if (paymentMode === SalesPaymentModesDropDown.CREDIT) {
    //     setAmtPaid("0");
    // }
    if (paymentMethod === "credit") {
      setAmtPaid("0");
    } else if (cash || card1 || card2 || onlinePayment1 || onlinePayment2) {
      const AmtPaid =
        parseFloat(cash || "0") +
        parseFloat(card1 || "0") +
        parseFloat(card2 || "0") +
        parseFloat(onlinePayment1 || "0") +
        parseFloat(onlinePayment2 || "0");
      setAmtPaid(AmtPaid.toString());
    }
  }, [
    paymentMode,
    cash,
    card1,
    card2,
    onlinePayment1,
    onlinePayment2,
    paymentMethod,
  ]);

  useEffect(() => {
    if (card1 || card2 || onlinePayment1 || onlinePayment2) {
      const backRecevied =
        parseFloat(card1 || "0") +
        parseFloat(card2 || "0") +
        parseFloat(onlinePayment1 || "0") +
        parseFloat(onlinePayment2 || "0");
      setBankRecevied(backRecevied.toString());
    } else {
      setBankRecevied("0");
    }
  }, [card1, card2, onlinePayment1, onlinePayment2]);

  useEffect(() => {
    if (paymentMethod === "credit") {
      const tenderedAmt = 0;
      setTenderedAmt(tenderedAmt.toString());
    }
  }, [paymentMethod]);

  const totalBal = useMemo(() => {
    return parseFloat(gtotal || "0") + parseFloat(previousBal || "0");
  }, [gtotal, previousBal]);

  const generateRemark = (payments: string[]) => {
    const remarkLabels = ["C", "C1", "C2", "C3", "C4"];
    const remarks = payments
      .map((payment, index) =>
        parseFloat(payment || "0") > 0 ? remarkLabels[index] : "",
      )
      .filter(Boolean);

    setRemark(remarks.join("+"));
    setSplit(remarks.length > 1 ? "Yes" : "No");
  };

  useEffect(() => {
    generateRemark([cash, card1, card2, onlinePayment1, onlinePayment2]);
  }, [totalBal, cash, card1, card2, onlinePayment1, onlinePayment2]);

  useEffect(() => {
    setTotalPoints(previousPoints + pointsEarned);
  }, [previousPoints, pointsEarned]);

  useEffect(() => {
    let d = new Date();
    const date = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    setPackNo(date + month);
  }, []);

  const onArea = () => {
    const addDynArea: DropDownType = {
      id: id,
      category: "area",
      title: areaName,
    };

    if (formType === FormTypeList.ADD) {
      dispatch(addDropDownAction(addDynArea))
        .then((text) => {
          toast("Added Division", ToastTypes.SUCCESS);
          setAreaName("");
        })
        .catch((text) => {
          toast(text, ToastTypes.ERROR);
        });
    }
    setAreaModal(false);
  };
  const onPlace = () => {
    const addDynPlace: DropDownType = {
      id: id,
      category: "place",
      title: placeName,
    };
    if (formType === FormTypeList.ADD) {
      dispatch(addDropDownAction(addDynPlace))
        .then((text) => {
          toast("Added Place", ToastTypes.SUCCESS);
          setPlaceName("");
        })
        .catch((text) => {
          toast(text, ToastTypes.ERROR);
        });
    }
    setPlaceModal(false);
  };
  const onDistrict = () => {
    const addDynDistrict: DropDownType = {
      id: id,
      category: "district",
      title: districtName,
    };

    if (formType === FormTypeList.ADD) {
      dispatch(addDropDownAction(addDynDistrict))
        .then((text) => {
          toast("Added District", ToastTypes.SUCCESS);
          setDistrictName("");
        })
        .catch((text) => {
          toast(text, ToastTypes.ERROR);
        });
    }
    setDistrictModal(false);
  };
  const onState = () => {
    const addDynState: DropDownType = {
      id: id,
      category: "state",
      title: stateName,
    };

    if (formType === FormTypeList.ADD) {
      dispatch(addDropDownAction(addDynState))
        .then((text) => {
          toast("Added State", ToastTypes.SUCCESS);
          setStateName("");
        })
        .catch((text) => {
          toast(text, ToastTypes.ERROR);
        });
    }
    setStateModal(false);
  };
  const onCountry = () => {
    const addDynCountry: DropDownType = {
      id: id,
      category: "country",
      title: countryName,
    };
    if (formType === FormTypeList.ADD) {
      dispatch(addDropDownAction(addDynCountry))
        .then((text) => {
          toast("Added Country", ToastTypes.SUCCESS);
          setCountryName("");
        })
        .catch((text) => {
          toast(text, ToastTypes.ERROR);
        });
    }
    setCountryModal(false);
  };
  const onRoute = () => {
    const addDynRoute: DropDownType = {
      id: id,
      category: "route",
      title: routeName,
    };
    if (formType === FormTypeList.ADD) {
      dispatch(addDropDownAction(addDynRoute))
        .then((text) => {
          toast("Added Route", ToastTypes.SUCCESS);
          setRouteName("");
        })
        .catch((text) => {
          toast(text, ToastTypes.ERROR);
        });
    }
    setRouteModal(false);
  };

  useEffect(() => {
    setBalance((totalBal - (amtPaid ? parseFloat(amtPaid) : 0))?.toString());
  }, [amtPaid, totalBal]);

  useEffect(() => {

    setTenderedReturn(
      (
        (tenderedAmt ? parseFloat(tenderedAmt) : 0) -
        (cash ? parseFloat(cash) : 0)
      )?.toString(),
    );
  }, [tenderedAmt, cash]);

  useEffect(() => {
    let val =
      (tenderedAmt ? parseFloat(tenderedAmt) : 0) -
      (tenderedReturn ? parseFloat(tenderedReturn) : 0);
    setTenderedBal(val?.toString());
  }, [tenderedReturn, tenderedAmt]);

  useEffect(() => {
    const data = salesItems.filter((f) => {
      return (
        f.productName?.label !== "" &&
        f.productName?.label !== undefined &&
        f.productName?.value !== "" &&
        f.productName?.value !== undefined
      );
    });
    const igst = data.reduce((previous, current) => {
      return previous + parseFloat(current?.gstValue!);
    }, 0);
    const nqty = data.reduce((previous, current) => {
      return previous + parseFloat(current?.qty!);
    }, 0);
    const ntax = data.reduce((previous, current) => {
      return previous + parseFloat(current?.taxable!);
    }, 0);
    const ngst = data.reduce((previous, current) => {
      return previous + parseFloat(current?.gstValue!);
    }, 0);
    const nsavings = data.reduce((previous, current) => {
      return previous + parseFloat(current?.disAmt!);
    }, 0);
    let val = (igst / 2).toFixed(2);
    setNetIgst((0)?.toString());
    setNetCgst(val.toString());
    setNetSgst(val.toString());
    setTotalItems(data?.length?.toString());
    setTotalQty(nqty?.toString());
    setNetTaxable(ntax?.toString());
    setNetGSTValue(ngst?.toFixed(2).toString());
    setYourSavings(nsavings?.toString());
  }, [salesItems]);

  useEffect(() => {
    const data = salesItems.filter((f) => {
      return (
        f.productName?.label !== "" &&
        f.productName?.label !== undefined &&
        f.productName?.value !== "" &&
        f.productName?.value !== undefined
      );
    });
    const tProfit = data
      .reduce((previous, current) => {
        return previous + parseFloat(current?.totalAmt!);
      }, 0)
      .toFixed(2);
    setBillNetAmt(tProfit.toString());
  }, [salesItems]);

  useEffect(() => {
    if (netAmt) {
      let Total = Math.round(parseFloat(netAmt)) - parseFloat(netAmt);
      setRoundOff(Total.toFixed(4));
    }
  }, [netAmt]);

  useEffect(() => {
    let totalAmount = 0;
    let totalDiscount = 0;

    salesItems
      .filter((f) => f.productName?.value)
      .forEach((item) => {
        totalAmount += parseFloat(item.totalAmt || "0");
        totalDiscount += parseFloat(item.disAmt || "0");
      });

    const baseTotal = totalAmount;
    const adjustedTotal =
      baseTotal + parseFloat(add || "0") - parseFloat(less || "0");
    const roundedTotal = Math.round(adjustedTotal);

    setTotalBillAmt(baseTotal.toFixed(2));
    setTotalDisAmt(totalDiscount.toFixed(2));
    setGTotal(roundedTotal.toString());
    setFinalAmount(roundedTotal.toString());
    setNetAmt(adjustedTotal.toFixed(2));
  }, [salesItems, add, less]);

  useEffect(() => {
    if (parseFloat(previousBal) < 0) {
      let cbal = -(
        (previousBal ? Math.abs(parseFloat(previousBal)) : 0) -
        parseFloat(amtPaid)
      );
      setCurrentBal(cbal.toString());
    } else {
      let cbal =
        (previousBal ? parseFloat(previousBal) : 0) - parseFloat(amtPaid);
      setCurrentBal(cbal.toString());
    }
  }, [amtPaid, previousBal]);
  useEffect(() => {
    let obal =
      (currentBal ? parseFloat(currentBal) : 0) +
      (previousBal ? parseFloat(previousBal) : 0);
    setOverAllBal(obal.toString());
  }, [currentBal, previousBal]);

  useEffect(() => {
    const newOverAllBalance =
      totalBal -
      ((parseFloat(cash) || 0) +
        (parseFloat(card1) || 0) +
        (parseFloat(card2) || 0) +
        (parseFloat(onlinePayment1) || 0) +
        (parseFloat(onlinePayment2) || 0));

    setLedgerBal(newOverAllBalance < 0 ? "0" : newOverAllBalance.toString());
  }, [totalBal, cash, card1, card2, onlinePayment1, onlinePayment2]);

  useEffect(() => {
    const d = new Date();
    const todayDate = d.getDate(); //27
    const currentMonth = d.getMonth() + 1; //6
    const currentYear = d.getFullYear(); //2022

    if (currentMonth >= 4 && todayDate >= 1) {
      let year = currentYear + "-" + (currentYear + 1);
      setFinancialYear(year);
    } else if (currentMonth < 4) {
      let year = currentYear - 1 + "-" + currentYear;
      setFinancialYear(year);
    }
  }, []);

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      // background: '#fff',
      // borderColor: '#9e9e9e',
      minHeight: "23px",
      height: "23px",
      boxShadow: state.isFocused ? null : null,
      width: "150px",
    }),

    valueContainer: (provided: any, state: any) => ({
      ...provided,
      height: "25px",
      padding: "0 5px",
    }),

    input: (provided: any, state: any) => ({
      ...provided,
      margin: "0px",
    }),
    dropdownIndicator: (prevStyle: any, state: any) => ({
      // ...prevStyle,
      display: "none",
    }),
    indicatorSeparator: (state: any) => ({
      display: "none",
    }),
    indicatorsContainer: (provided: any, state: any) => ({
      ...provided,
      height: "28px",
    }),
    menu: (css: any) => ({
      ...css,
      // 'z-index': 600,
      width: "250px",
    }),
  };
  const customStyles2 = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: "23px",
      height: "23px",
      boxShadow: state.isFocused ? null : null,
      // zIndex: 500,
      width: "250px",
    }),
    valueContainer: (provided: any, state: any) => ({
      ...provided,
      height: "25px",
      padding: "0 6px",
    }),
    input: (provided: any, state: any) => ({
      ...provided,
      margin: "0px",
    }),
    dropdownIndicator: (prevStyle: any, state: any) => ({
      // ...prevStyle,
      display: "none",
    }),
    indicatorSeparator: (state: any) => ({
      display: "none",
    }),
    indicatorsContainer: (provided: any, state: any) => ({
      ...provided,
      height: "28px",
    }),
    menu: (css: any) => ({
      ...css,
      zIndex: 555,
      width: "250px",
    }),
  };

  useEffect(() => {
    setAmtInWords(convertAmtToWords(parseFloat(gtotal), "India"));
  }, [gtotal]);

  useEffect(() => {
    const fetchBalance = async () => {
      if (customer) {
        try {
          const response = await getTransactionCurrentBalanceByAcNo(
            customer.value?.toString(),
          );
          const data = response.data.data;

          const balance = Array.isArray(data)
            ? data[0]?.currentBalance
            : data.currentBalance;

          const formattedBalance =
            balance && parseFloat(balance) >= 0 ? balance.toString() : "0";

          setPreviousBal(formattedBalance);
        } catch (error) {
          console.error("Error fetching balance:", error);
          setPreviousBal("0");
        }
      } else {
        setPreviousBal("0");
      }
    };

    fetchBalance();
  }, [customer]);

  useEffect(() => {
    if (formType === FormTypeList.EDIT && id) {
      (async () => {
        try {
          setShowOverlayLoader();
          const response = await getSalesById(id);          
          const data: SalesEditAttributes = response.data.data;
          const custobj = customerList?.find(
            (a) => a.accountNumber?.toString() === data.customer_id?.toString(),
          );
          let custname = custobj?.accountName;
          let custadd = custobj?.address1! + custobj?.address2;
          let custcont = custobj?.contactNumber1;
          let custcont2 = custobj?.contactNumber2;
          
          let cScore = custobj?.score;
          let customerGst = custobj?.gstNo;
          let customerPlace = custobj?.place;
          // let sgst = custobj?.gstNumber
          let custplace = custobj?.supplierPlace;

          const userObj = userList.find(
            (a) => a.id?.toString() === data.created_by?.toString(),
          );
          let uname = userObj?.name ? userObj?.name : "";

          //****top****
          setInvoiceType(data.invoice_type);
          setInvoiceTitle(data.invoice_title);
          setBillNo(data.invoice_number.toString());
          // setBillTime(data.invoice_title)
          setBillDate(new Date(data.invoice_date));
          setInvoiceAmount(data.grand_total);
          setSalesEntryNo(data.sales_entry_number?.toString());
          setCustomer({
            value: data.customer_id,
            label: custname ? custname : "",
            name: custname ? custname : "",
            conNo: custcont ? custcont.toString() : "",
            conNo2: custcont2 ? custcont2.toString() : "",
            score: cScore ? cScore : "",
            gstNo: customerGst ? customerGst : "",
            place: customerPlace ? customerPlace : ""
          });
          // setCustomerNo('2')
          setCustomerName(custname!);
          setCustomerAddress(custadd);
          setCustomerContact(custcont?.toString()!);
          setCustomerPlace(custplace ? custplace : "");
          setPreviousPoints(cScore ? parseInt(cScore) : 0);
          // setGST(sgst ? sgst : '')
          // setSupAadhar(saadhar ? saadhar?.toString() : '')
          setRefBy(data.rep_id?.toString());
          setBranch(data.branch_id?.toString());
          setBilledBy(uname);
          setSysNo(data.system_no);
          setPlaceOfSupply(
            data.place_of_supply && data.place_of_supply !== ""
              ? data.place_of_supply
              : DEFAULT_PLACE_OF_SUPPLY
          );

          // Process Transactions
          let cashAmount = "0",
            card1Amount = "0",
            card2Amount = "0",
            onlinePayment1Amount = "0",
            onlinePayment2Amount = "0";
          let paymentMethodByPaymentMode = "";

          data?.Transactions?.forEach((transaction) => {
            switch (transaction.particulars) {
              case "Cash":
                cashAmount = transaction.credit_amount || "0";
                break;
              case "Card1":
                card1Amount = transaction.credit_amount || "0";
                break;
              case "Card2":
                card2Amount = transaction.credit_amount || "0";
                break;
              case "Online Payment 1":
                onlinePayment1Amount = transaction.credit_amount || "0";
                break;
              case "Online Payment 2":
                onlinePayment2Amount = transaction.credit_amount || "0";
                break;
            }

            if (transaction.payment_mode === "Credit") {
              paymentMethodByPaymentMode = "credit";
            } else if (["Cash", "Bank"].includes(transaction.payment_mode)) {
              paymentMethodByPaymentMode = "cashAndBank";
            }
          });

          // Set Transaction States
          setCash(cashAmount);
          setCard1(card1Amount);
          setCard2(card2Amount);
          setOnlinePayment1(onlinePayment1Amount);
          setOnlinePayment2(onlinePayment2Amount);
          setPaymentMethod(paymentMethodByPaymentMode);

          // Process Sales Items
          const salesItemEditData = getDefaultSalesItems();

          for (let pindex = 0; pindex < data.salesItems.length; pindex++) {
            const pItem = data.salesItems[pindex];
            const { productCode: pc, batchNumber: bn } = salesItemRowCodes(pItem);
            // const stockObj =
            //   StockList.find(
            //     (pd) =>
            //       pd.productCode?.toString() === pc &&
            //       pd.batchNumber?.toString() === bn,
            //   ) ||
            //   StockList.find((pd) => pd.productCode?.toString() === pc);
            // const modelNumber = await resolveSalesItemModelNumber(
            //   pItem,
            //   stockObj,
            // );

            const matches = await db.products
                          .where("productCode")
                          .equals(pItem.product_code)
                          .toArray();
            
            console.log("mat", matches);
            console.log("length", matches.length);

            let dbProduct: any = null;

            if (matches.length) {
              const byBatch = matches.find(
                (m) =>
                  String(m.batchNumber ?? "") ===
                  String(pItem.batch_number ?? "")
              );

              console.log("by batch", byBatch);

              const hit = byBatch ?? matches[0];

              console.log("hit", hit);

              const m = hit?.modelNumber;

              if (
                m !== undefined &&
                m !== null &&
                String(m) !== ""
              ) {
                dbProduct = hit;
              } else {
                dbProduct = matches[0];
              }
            }

            if (!dbProduct && matches.length) {
              dbProduct = matches[0];
            }

            if (!dbProduct) {
              console.log(
                "Skipping: No valid dbProduct found for",
                pItem.product_code
              );
              return;
            }
            
            salesItemEditData[pindex] = {
              id: pindex + 1,
              productName: {
                label: dbProduct?.productName || "",
                value: dbProduct?.id.toString() || "",
              },
              productId: 0,
              productCode: pc,
              modelNumber: dbProduct?.modelNumber ?? '',
              productGroupCode: dbProduct?.productCode?.toString() || "",
              gstPercent: dbProduct?.gst?.toString() || "",
              batchNo: dbProduct?.batchNumber?.toString() || bn || "",
              pdPercent: parseFloat(
                pItem.p_discount_percent?.toString() || "0",
              ),
              pdAmount: parseFloat(pItem.p_discount_amount?.toString() || "0"),
              expiryDt: dbProduct?.expiryDate
                ? new Date(dbProduct?.expiryDate)
                : null,
              hsn: dbProduct?.hsnCode?.toString() || "",
              qty: pItem.quantity?.toString() || "",
              pwog: pItem.price_wogst?.toString() || "",
              pwg: pItem.price_gst?.toString() || "",
              rate: pItem.w_rate?.toString() || "",
              mrp: pItem.mrp?.toString() || "",
              igst: pItem.igst_amount?.toString() || "0",
              cgst: pItem.cgst_amount?.toString() || "0",
              sgst: pItem.sgst_amount?.toString() || "0",
              disAmt: pItem.s_discount?.toString() || "0",
              gstValue: pItem.gst_value?.toString() || "0",
              taxable: pItem.taxable_amount?.toString() || "0",
              totalAmt: pItem.total_amount?.toString() || "0",
              profitPercent: pItem.profit_percent?.toString() || "0",
            };
          }

          setSalesItems(salesItemEditData);

          //****Right****
          setNetTaxable(data.net_taxable);
          setNetIgst(data.net_igst);
          setNetSgst(data.net_sgst);
          setNetCgst(data.net_cgst);
          setBillAmount(data.bill_amount);
          setNetAmt(data.bill_amount);
          setDisPercent(data.dis_percent?.toString());
          setDisAmt(data.dis_amount?.toString());
          setTenderedAmt(data.tender_amount?.toString());
          setTenderedReturn(data.tender_return?.toString());
          // setBillNetAmt(data.)
          setAdd(data.add);
          setLess(data.less);
          setGTotal(data.grand_total);
          setAmtInWords(data.amt_in_words);
          if (data.payment_mode === SalesPaymentModesDropDown.CASH) {
            setPaymentMode(SalesPaymentModesDropDown.CASH);
          } else if (data.payment_mode === SalesPaymentModesDropDown.BANK) {
            setPaymentMode(SalesPaymentModesDropDown.BANK);
            setBankName(
              data?.Transactions ? data?.Transactions[2]?.account_name : "",
            );
            setBankNumber(
              data?.Transactions
                ? data?.Transactions[2]?.account_number?.toString()
                : "",
            );
          } else if (data.payment_mode === SalesPaymentModesDropDown.CREDIT) {
            setPaymentMode(SalesPaymentModesDropDown.CREDIT);
          }
          setPaymentNo(data.particulars);
          setAmtPaid(data.amt_paid);
          // setCurrentBal('')
          // setPreviousBal('-3800')
          // setOverAllBal('')
          // setTotalItems(data.delivery_mode)
          // setTotalQty(data.delivery_details)
          setFinalAmount(data.grand_total);
          setGTotal(data.grand_total);
          setTotalItems(data.salesItems?.length?.toString());
          const totalQuantity = data.salesItems.reduce(
            (total, item) => total + (item.quantity || 0),
            0,
          );
          setTotalQty(totalQuantity.toString());
          setPaymentNo(data.particulars);
          setTotalBillAmt(data.total_amount);
          setTotalDisAmt(data.total_discount);
          setPreviousBal(data.prev_balance);
          // setTotal(data.total_balance);
          setBalance(data.credit);
          setBankRecevied(data.bank_received);
        } catch (error) {
          console.error("Error fetching sales data:", error);
        } finally {
          setHideOverlayLoader();
        }
      })();
    }
  }, [formType, id]);

  const onCustomer = () => {
    if(contactNumber1 && contactNumber1?.length !== 10) {
      setContactNumber1Error("Contact No must be 10 digits");
      return;
    }
    if(contactNumber1 && !isMobileNo(contactNumber1)) {
      setContactNumber1Error("Enter a valid contact no");
      return;
    }
    const addData: CustomerType = {
      branchId: parseInt(branchId),
      nameTag: nameTag,
      accountName: accountName,
      contactNumber1: contactNumber1?.trim()
        ? parseInt(contactNumber1, 10)
        : '',
      contactNumber2: contactNumber2?.trim()
        ? contactNumber2
        : '',
      address1: address1,
      address2: address2,
      area: area,
      place: place,
      district: district,
      pincode: parseInt(pincode !== undefined ? pincode : ""),
      state: state,
      country: country,
      route: route,
      aadharNumber: parseInt(aadharNumber !== undefined ? aadharNumber : ""),
      gstNo: gstNumber !== undefined ? gstNumber : "",
      // branch: `${AuthUser?.branchId}`
    };

    if (formType === FormTypeList.ADD) {
      dispatch(addCustomerAction(addData))
        .then((text) => {
          toast(text, ToastTypes.SUCCESS);
          api()
            .get(`/customer/recent/`)
            .then((res) => {
              const result: CustomerType = res.data.data;

              setCustomer({
                value: result?.accountNumber!,
                label: result?.accountName?.toString() ?? "",
                name: result?.accountName?.toString() ?? "",
                conNo: result?.contactNumber1?.toString() ?? "",
                conNo2: result?.contactNumber2?.toString() ?? "",
                score: result?.score != null ? String(result.score) : "0",
                gstNo: result?.gstNo?.toString() ?? "",
                place: result?.place?.toString() ?? ""
              });
              setCustomerName(result?.accountName?.toString() ?? "");
              setCustomerContact(result?.contactNumber1?.toString() ?? "");
              setCustomerContact2(result?.contactNumber2?.toString() ?? "");
              setPreviousPoints(
                parseInt(
                  result?.score != null ? String(result.score) : "0",
                  10,
                ),
              );
            })
            .finally(() => {
              void fetchCustomer();
            });
          setBranch("");
          setNameTag("");
          setAccountName("");
          setContactNumber1("");
          setContactNumber2("");
          setAddress1("");
          setAddress2("");
          setArea("");
          setPlace("");
          setDistrict("");
          setPincode("");
          setState("");
          setCountry("");
          setRoute("");
          setModal(false);
        })
        .catch((text) => {
          toast(text, ToastTypes.ERROR);
        });
    }
  };

  useEffect(() => {
    if (!customerList?.length) return;
    // Do not replace an active selection when the list refetches (e.g. after add customer).
    if (customer !== null) return;

    let DefaultCustomer = customerList?.find(
        (c) => c.accountName?.toLocaleLowerCase() === "cash sales",
      );
      if (DefaultCustomer) {
        setCustomer(
          DefaultCustomer
            ? {
                label:
                  (DefaultCustomer?.accountName?.toString() ?? "") +
                  "-" +
                  (DefaultCustomer?.contactNumber1?.toString() ?? ""),
                name: DefaultCustomer?.accountName?.toString() ?? "",
                value: DefaultCustomer?.accountNumber
                  ? DefaultCustomer?.accountNumber
                  : 0,
                conNo:
                  DefaultCustomer?.contactNumber1?.toString() ?? "",
                conNo2:
                  DefaultCustomer?.contactNumber2?.toString() ?? "",
                score: DefaultCustomer?.score ? DefaultCustomer?.score : "0",
                gstNo: DefaultCustomer?.gstNo ? DefaultCustomer?.gstNo : "NIL",
                place: DefaultCustomer?.place ? DefaultCustomer?.place : ""
              }
            : null,
        );
        setCustomerName(
          DefaultCustomer?.accountName?.toString() ?? "",
        );
        setCustomerContact(
          DefaultCustomer?.contactNumber1?.toString() ?? "",
        );
        setPreviousPoints(
          DefaultCustomer?.score ? parseInt(DefaultCustomer?.score) : 0,
        );
      }
  }, [customerList]);

  const handleNonNegativeNumberInput = (setter: (val: string) => void) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      // Remove any minus sign or invalid characters
      if (value.startsWith("-")) {
        value = value.substring(1);
      }
      // Allow only digits and at most one decimal point
      value = value.replace(/[^0-9.]/g, "");
      // Prevent multiple decimal points
      const parts = value.split(".");
      if (parts.length > 2) {
        value = parts[0] + "." + parts.slice(1).join("");
      }
      setter(value === "" ? "" : value);
    };
  };

  useEffect(() => {
    const totalBalNum = parseFloat(totalBal.toString()) || 0;
    const tender = parseFloat(tenderedAmt) || 0;
    const c1 = parseFloat(card1) || 0;
    const c2 = parseFloat(card2) || 0;
    const o1 = parseFloat(onlinePayment1) || 0;
    const o2 = parseFloat(onlinePayment2) || 0;

    const nonCashTotal = c1 + c2 + o1 + o2;
    const maxAllowedCash = Math.max(0, totalBalNum - nonCashTotal);

    // Cash actually applied = min(tender, maxAllowedCash)
    const actualCash = Math.min(tender, maxAllowedCash);
    const totalPayments = actualCash + nonCashTotal;

    const tenderReturn = Math.max(0, tender - maxAllowedCash); // only excess cash is returned
    const credit = Math.max(0, totalBalNum - totalPayments);

    setCash(actualCash.toFixed(2));
    setTenderedReturn(tenderReturn.toFixed(2));
    setBalance(credit.toFixed(2)); // credit field

    const bankReceived = nonCashTotal;
    setBankRecevied(bankReceived.toFixed(2));

    generateRemark([
      actualCash.toString(),
      card1,
      card2,
      onlinePayment1,
      onlinePayment2,
    ]);
  }, [tenderedAmt, card1, card2, onlinePayment1, onlinePayment2, totalBal]);

  const handlePrintDirectly = () => {
    if (parseFloat(gtotal) <= 0) {
      toast("Please fill the items first", ToastTypes.ERROR);
      return;
    }

    // 1. Build the print object from current state
    const printObj = {
      customer: {
        name: customerName || customer?.name || "Cash Sales",
        conNo: customerContact || customer?.conNo || "",
        conNo2: customerContact2 || customer?.conNo2 || "",
        gstNo: customer?.gstNo || "NIL",
        place: customer?.place
      },
      billnumber: billNo || "0",
      date: LocalFormatDate(getDate(new Date())),
      time: new Date().toLocaleTimeString("en-US", {
        timeZone: "Asia/Kolkata",
        hour12: true,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      productitems: salesItems
        .filter((item) => item.productName !== null && item.productName?.value)
        .map((item) => {
          const qty = parseFloat(item.qty || "0");
          const rate = parseFloat(item.rate || "0");
          const discountPerUnit = parseFloat(item.disAmt || "0");
          const totalAfterDiscount = Math.max(0, (rate - discountPerUnit) * qty);
          const gstPercent = parseFloat(item.gstPercent || "0");
          const taxable =
            gstPercent >= 0
              ? totalAfterDiscount / (1 + gstPercent / 100)
              : totalAfterDiscount;
          return {
            name: item.productName?.label || "",
            qty: item.qty,
            rate: rate.toFixed(2),
            disc: discountPerUnit.toFixed(2),
            total: totalAfterDiscount.toFixed(2),
            taxable: taxable.toFixed(2),
            gst: item.gstPercent || "0",
            hsn: item.hsn || "-",
          };
        }),
      taxable: netTaxable || "0",
      cgst: netCgst || "0",
      sgst: netSgst || "0",
      previousBal: previousBal || "0",
      finaltotal: finalAmount || "0",
      paymentamount: "0", // Not processed yet
      credit: "0",
      tender: "0",
      balance: "0",
    };

    // 2. Generate PDF & Open Print Dialog (thermal uses USB print service after save instead)
    const pdfBlob = generateBillPdf(printObj);
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl, "_blank");
    
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    } else {
      toast("⚠️ Please allow popups to print the invoice.");
    }
    
    // Clean up memory
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 3000);
  };

  return (
    <>
      <Modal
        headerText="Sales"
        onClose={handleFormClose}
        centered
        size="xl"
        visible={showSalesView}
      >
        <Sales
          setFormType={setFormType}
          setId={setId}
          onclose={handleFormClose}
          viewOpen={showSalesView}
          onEditComplete={() => dispatch(fetchSalesAction())}
        />
      </Modal>
      <Modal
        headerText={"Add Area"}
        visible={areaModal}
        onClose={handleModalClose}
        centered
        size="sm"
      >
        <FormInput
          name="areaName"
          value={areaName}
          onChange={handleAreaName}
          className="mb-3"
        />
        <button
          className="btn btn-sm fw-bold btn-danger px-2"
          onClick={() => resetStates()}
        >
          Add New
        </button>
        <button
          type="button"
          className="btn btn-sm fw-bold btn-primary m-2"
          onClick={onArea}
        >
          save
        </button>
      </Modal>
      <Modal
        headerText={"Add Place"}
        visible={placeModal}
        onClose={handleModalClose}
        centered
        size="sm"
      >
        <FormInput
          name="placeName"
          value={placeName}
          onChange={handlePlaceName}
          className="mb-3"
        />
        <button
          className="btn btn-sm fw-bold btn-danger px-2"
          onClick={() => resetStates()}
        >
          Add New
        </button>
        <button
          type="button"
          className="btn btn-sm fw-bold btn-primary m-2"
          onClick={onPlace}
        >
          save
        </button>
      </Modal>
      <Modal
        headerText={"Add District"}
        visible={districtModal}
        onClose={handleModalClose}
        centered
        size="sm"
      >
        <FormInput
          name="districtName"
          value={districtName}
          onChange={handleDistrictName}
          className="mb-3"
        />
        <button
          className="btn btn-sm fw-bold btn-danger px-2"
          onClick={() => resetStates()}
        >
          Add New
        </button>
        <button
          type="button"
          className="btn btn-sm fw-bold btn-primary m-2"
          onClick={onDistrict}
        >
          save
        </button>
      </Modal>
      <Modal
        headerText={"Add State"}
        visible={stateModal}
        onClose={handleModalClose}
        centered
        size="sm"
      >
        <FormInput
          name="stateName"
          value={stateName}
          onChange={handleStateName}
          className="mb-3"
        />
        <button
          className="btn btn-sm fw-bold btn-danger px-2"
          onClick={() => resetStates()}
        >
          Add New
        </button>
        <button
          type="button"
          className="btn btn-sm fw-bold btn-primary m-2"
          onClick={onState}
        >
          save
        </button>
      </Modal>
      <Modal
        headerText={"Add Country"}
        visible={countryModal}
        onClose={handleModalClose}
        centered
        size="sm"
      >
        <FormInput
          name="countryName"
          value={countryName}
          onChange={handleCountryName}
          className="mb-3"
        />
        <button
          className="btn btn-sm fw-bold btn-danger px-2"
          onClick={() => resetStates()}
        >
          Add New
        </button>
        <button
          type="button"
          className="btn btn-sm fw-bold btn-primary m-2"
          onClick={onCountry}
        >
          save
        </button>
      </Modal>
      <Modal
        headerText={"Add Route"}
        visible={routeModal}
        onClose={handleModalClose}
        centered
        size="sm"
      >
        <FormInput
          name="routeName"
          value={routeName}
          onChange={handleRouteName}
          className="mb-3"
        />
        <button
          className="btn btn-sm fw-bold btn-danger px-2"
          onClick={() => resetStates()}
        >
          Add New
        </button>
        <button
          type="button"
          className="btn btn-sm fw-bold btn-primary m-2"
          onClick={onRoute}
        >
          save
        </button>
      </Modal>
      <Modal
        headerText={"Customer"}
        visible={modal}
        onClose={handleFormClose}
        centered
        size="lg"
      >
        <div className="row">
          <div className="col-4">
            <FormInput
              name="branchId"
              label="Branch Name"
              labelClassName="required"
              required={true}
              value={branchId}
              onChange={handleBranch}
              errorText={branchIdError}
              type="select"
            >
              <option value="">Select Branch</option>
              {BranchList.map((br: BranchType, i: any) => {
                return (
                  <option value={br.id} key={i}>
                    {br.name}
                  </option>
                );
              })}
              {/* {clients?.map((client) => (
                                <option key={client.id} value={client.id}>
                                    {client.place}
                                </option>
                            ))} */}
            </FormInput>
          </div>
          <div className="col-2">
            <FormInput
              name="nameTag"
              label="Name Tag"
              // labelClassName="required"
              // required={true}
              value={nameTag}
              onChange={handleTag}
              errorText={nameTagError}
              type="select"
            >
              <option value="">Select Tag</option>
              {[
                "Mr.",
                "Mrs.",
                "Miss.",
                "Master.",
                "Baby.",
                "Dr.",
                "Er.",
                "N/A",
              ].map((tag, i) => {
                return (
                  <option value={tag} key={i}>
                    {tag}
                  </option>
                );
              })}
            </FormInput>
          </div>

          <div className="col-6">
            <FormInput
              name="accountName"
              label="Account Name"
              // required={true}
              value={accountName}
              onChange={handleAccountName}
              errorText={accountNameError}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <FormInput
              name="contactNumber1"
              label="Primary Contact Number"
              // labelClassName="required"
              // required={true}
              value={contactNumber1}
              onChange={handleContactNumber1}
              errorText={contactNumber1Error}
            />
          </div>
          <div className="col-6">
            <FormInput
              name="contactNumber2"
              label="Vehicle No"
              labelClassName="required"
              value={contactNumber2?.toUpperCase()}
              onChange={handleContactNumber2}
              errorText={contactNumber2Error}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <FormInput
              name="address1"
              label="Address 1"
              labelClassName="required"
              value={address1}
              onChange={handleAddress1}
              errorText={address1Error}
            />
          </div>
          <div className="col-6">
            <FormInput
              name="address2"
              label="Address 2"
              labelClassName="required"
              value={address2}
              onChange={handleAddress2}
              errorText={address2Error}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-4">
            <FormInput
              name="area"
              label="Area"
              labelClassName="required"
              value={area}
              onChange={handleArea}
              errorText={areaError}
              type="select"
              icon={
                <i
                  data-toggle="modal"
                  className={`fe-plus-circle me-1 p-1`}
                  onClick={() => setAreaModal(true)}
                >
                  <div className="modal fade"></div>
                </i>
              }
            >
              <option value="">Select</option>
              {DropDownList?.filter((ddl) => ddl.category === "area")?.map(
                (ddl, i) => {
                  return (
                    <option value={ddl.title} key={i}>
                      {ddl.title}
                    </option>
                  );
                },
              )}
            </FormInput>
          </div>
          <div className="col-4">
            <FormInput
              name="place"
              label="Place"
              labelClassName="required"
              value={place}
              onChange={handlePlace}
              errorText={placeError}
              type="select"
              icon={
                <i
                  data-toggle="modal"
                  className={`fe-plus-circle me-1 p-1 cursor-pointer`}
                  onClick={() => setPlaceModal(true)}
                >
                  <div className="modal fade"></div>
                </i>
              }
            >
              <option value="">Select</option>
              {DropDownList?.filter(
                (ddl: DropDownType) => ddl.category === "place",
              )?.map((ddl, i) => {
                return (
                  <option value={ddl.title} key={i}>
                    {ddl.title}
                  </option>
                );
              })}
            </FormInput>
          </div>
          <div className="col-4">
            <FormInput
              name="district"
              label="District"
              labelClassName="required"
              value={district}
              onChange={handleDistrict}
              errorText={districtError}
              type="select"
              icon={
                <i
                  data-toggle="modal"
                  className={`fe-plus-circle me-1 p-1`}
                  onClick={() => setDistrictModal(true)}
                >
                  <div className="modal fade"></div>
                </i>
              }
            >
              <option value="">Select</option>
              {DropDownList?.filter(
                (ddl: DropDownType) => ddl.category === "district",
              )?.map((ddl, i) => {
                return (
                  <option value={ddl.title} key={i}>
                    {ddl.title}
                  </option>
                );
              })}
            </FormInput>
          </div>
          <div className="col-4">
            <FormInput
              name="state"
              label="State"
              labelClassName="required"
              value={state}
              onChange={handleState}
              errorText={stateError}
              type="select"
              icon={
                <i
                  data-toggle="modal"
                  className={`fe-plus-circle me-1 p-1`}
                  onClick={() => setStateModal(true)}
                ></i>
              }
            >
              <option value="">Select</option>
              {DropDownList?.filter(
                (ddl: DropDownType) => ddl.category === "state",
              )?.map((ddl, i) => {
                return (
                  <option value={ddl.title} key={i}>
                    {ddl.title}
                  </option>
                );
              })}
            </FormInput>
          </div>
          <div className="col-4">
            <FormInput
              name="country"
              label="Country"
              labelClassName="required"
              value={country}
              onChange={handleCountry}
              errorText={countryError}
              type="select"
              icon={
                <i
                  data-toggle="modal"
                  className={`fe-plus-circle me-1 p-1`}
                  onClick={() => setCountryModal(true)}
                >
                  <div className="modal fade"></div>
                </i>
              }
            >
              <option value="">Select</option>
              {DropDownList?.filter(
                (ddl: DropDownType) => ddl.category === "country",
              )?.map((ddl, i) => {
                return (
                  <option value={ddl.title} key={i}>
                    {ddl.title}
                  </option>
                );
              })}
            </FormInput>
          </div>

          <div className="col-4">
            <FormInput
              name="route"
              label="Route"
              labelClassName="required"
              value={route}
              onChange={handleRoute}
              errorText={routeError}
              type="select"
              icon={
                <i
                  data-toggle="modal"
                  className={`fe-plus-circle me-1 p-1`}
                  onClick={() => setRouteModal(true)}
                >
                  <div className="modal fade"></div>
                </i>
              }
            >
              <option value="">Select</option>
              {DropDownList?.filter(
                (ddl: DropDownType) => ddl.category === "route",
              )?.map((ddl, i) => {
                return (
                  <option value={ddl.title} key={i}>
                    {ddl.title}
                  </option>
                );
              })}
            </FormInput>
          </div>
        </div>
        <div className="row">
          <div className="col-4">
            <FormInput
              name="aadharNumber"
              label="Aadhar Number"
              labelClassName="required"
              value={aadharNumber}
              onChange={handleAadharNumber}
              errorText={aadharNumberError}
              type="text"
            />
          </div>
          <div className="col-4">
            <FormInput
              name="gstNumber"
              label="GST Number"
              labelClassName="required"
              value={gstNumber}
              onChange={handlegstNumber}
              errorText={gstNumberError}
              type="text"
            />
          </div>
        </div>

        <div className="text-end">
          <button
            className="btn btn-sm fw-bold btn-danger"
            onClick={() => resetStates()}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn-sm fw-bold btn-primary m-2"
            onClick={onCustomer}
          >
            Save
          </button>
        </div>
      </Modal>

      <UserLayout>
        {/* <h4>Sales Master</h4> */}
        <div className="row">
          <div className="col-lg-9">
            {/* <div className="col-lg-12"> */}
            <div className="card shadow-lg">
              <div className="card-body p-1">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="col-lg-12 hstack">
                      <div className="col-3">
                        <label className="">Contact Number</label>
                      </div>
                      <div className="col-8 mt-1 mb-1">
                        <Select
                          options={
                            customerList?.map((f) => {
                              const accountName = decodeHtml(f?.accountName || "");
                              const primaryContact = f?.contactNumber1
                                ? f.contactNumber1?.toString() ?? ""
                                : "";
                              const vehicleNo = f?.contactNumber2
                                ? f.contactNumber2?.toString() ?? ""
                                : "";
                              const label = accountName
                                ? `${accountName}${primaryContact ? ` - ${primaryContact}` : ""}`
                                : vehicleNo
                                ? `Vehicle No: ${vehicleNo}${primaryContact ? ` - ${primaryContact}` : ""}`
                                : primaryContact
                                ? `Contact: ${primaryContact}`
                                : `Customer ${f?.accountNumber ?? ""}`;

                              return {
                                label,
                                name: accountName || "",
                                value: f?.accountNumber || 0,
                                score: f?.score ? f?.score?.toString() : "0",
                                conNo: primaryContact,
                                conNo2: vehicleNo,
                                gstNo: f.gstNo ? f.gstNo?.toString() : "",
                                place: f.place ? f.place?.toString() : "",
                              };
                            }) || []
                          }
                          isSearchable={true}
                          isClearable={true}
                          onChange={(v) => {
                            setCustomer(v);
                            setCustomerName(v?.name ? decodeHtml(v.name) : "");
                            setCustomerContact(v?.conNo?.toString()!);
                            setCustomerContact2(v?.conNo2.toString()!);
                            setPreviousPoints(
                              v?.score ? parseInt(v?.score!) : 0,
                            );
                          }}
                          value={customer}
                          styles={customStyles2}
                        />
                      </div>
                      <div className="col-1">
                        {/* {<i data-toggle="modal" className={`fe-plus-circle me-1 p-1 cursor-pointer`} onClick={() => setModal(true)}>
                                                    <div className="modal fade"></div>
                                                </i>} */}
                        <button
                          title="Add New Customer"
                          className="btn btn-sm btn-warning "
                          onClick={() => setModal(true)}
                        >
                          <UserPlus size={15} width={18} />
                        </button>
                      </div>
                    </div>

                    <div className="col-lg-12 hstack">
                      <div className="col-3">
                        <label htmlFor="">Customer Name</label>
                      </div>
                      <div className="col-9 mt-1">
                        <FormInput
                          name="customerName"
                          label=""
                          labelClassName="required"
                          value={decodeHtml(customerName || '')}
                          // onChange={handleSupName}
                          errorText={customerNameError}
                          containerClass="mb-1 text-end"
                        />
                      </div>
                    </div>
                    <div className="col-lg-12 hstack">
                      <div className="col-3">
                        <label htmlFor="">Referred By</label>
                      </div>
                      <div className="col-9 mt-1">
                        <FormInput
                          name="Referred By"
                          label=""
                          labelClassName="required "
                          value={refBy}
                          onChange={handleRefBy}
                          placeholder="Referred By"
                          errorText={refByError}
                          containerClass="mb-1"
                          // ref={refByRef}
                          // onKeyDown={(e) => handleKeyDown(e, invoiceDateRef, 'invoiceDate')}
                          className={"form-control"}
                          // className={focusedField === 'refBy' ? "border border-4 border-primary rounded" : ""}
                          // disabled={showViewForm}
                          type="select"
                        >
                          <option value="">Select</option>
                          {AccountLists?.filter(
                            (al) =>
                              al.groupName === "representer" ||
                              al.groupName === "Representer",
                          ).sort((a, b) => (a.accountName || '').localeCompare(b.accountName || '')).map((al, i) => {
                            return (
                              <option value={al.accountNumber} key={i}>
                                {al.accountName}
                              </option>
                            );
                          })}
                        </FormInput>
                      </div>
                    </div>
                    <div className="col-lg-12">
                      {/* <div className="col-10"> */}
                      <h4 className=" fs-4 badge bg-info">
                        Customer Points : {previousPoints}
                      </h4>
                      <h4
                        className=" fs-4 badge bg-info"
                        style={{ marginLeft: "5px" }}
                      >
                        Current Balance : {currentBal}
                      </h4>
                      {/* </div> */}
                      {previousPoints > 0 && (
                        <button
                          className="ms-3 btn btn-sm fw-bold btn-success"
                          onClick={redeemCustomerPoints}
                        >
                          Redeem
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="col-lg-6">
                    {/* <div className="col-lg-12 hstack">
                                        <div className="col-5">
                                            <label htmlFor="">Customer No </label>
                                        </div>
                                        <div className="col-7">
                                            <FormInput
                                                name='customerNo'
                                                label=''
                                                labelClassName="required"
                                                value={customerNo}
                                                // onChange={handleInvoiceNo}
                                                errorText={customerNoError}
                                                containerClass="mb-1 text-end"
                                                width={50}

                                            />
                                        </div>
                                    </div> */}
                    {/* <div className="col-3">
                                            <label htmlFor="">Contact Number</label>
                                        </div>
                                        <div className="col-8 mt-1">
                                            <FormInput
                                                name='customerContact'
                                                label=''
                                                labelClassName="required"
                                                value={customerContact}
                                                onChange={handleCustomerContact}
                                                errorText={customerContactError}
                                                containerClass="mb-1"
                                                width={50}

                                            />
                                        </div> */}
                    <div className="col-lg-12 hstack">
                      <div className="col-3">
                        <label htmlFor="">Financial Year </label>
                      </div>
                      <div className="col-9">
                        <FormInput
                          name="year"
                          label=""
                          labelClassName="required"
                          value={financialYear}
                          // onChange={handleProductName}
                          // errorText={financialYearError}
                          containerClass="mb-1 text-end"
                          width={50}
                          readOnly={true}
                        />
                      </div>
                    </div>

                    <div className="col-lg-12 hstack">
                      <div className="col-3">
                        <label htmlFor="">Sales Type</label>
                      </div>
                      <div className="col-9 mt-1">
                        <FormInput
                          name="Sales Type"
                          label=""
                          labelClassName="required"
                          value={invoiceType}
                          onChange={handleInvoiceType}
                          placeholder="Sales Type"
                          errorText={invoiceTypeError}
                          containerClass="mb-1"
                          // disabled={showViewForm}
                          type="select"
                        >
                          <option value="">Select</option>
                          {SALES_TYPE_DROPS.map((a, i) => {
                            return (
                              <Option value={a.value.toString()} key={i}>
                                {a.text}
                              </Option>
                            );
                          })}
                        </FormInput>
                      </div>
                    </div>

                    <div className="col-lg-12 hstack">
                      <div className="col-3">
                        <label htmlFor="discountInput">Discount</label>
                      </div>
                      <div className="col-9 mt-1">
                        <input
                          id="discountInput"
                          className="form-control"
                          type="number"
                          list="discountOptions"
                          value={discountValue}
                          onChange={(e) => setDiscountValue(e.target.value)}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                        <datalist id="discountOptions">
                          <option value="0" label="0%"></option>
                          <option value="5" label="5%"></option>
                          <option value="12" label="12%"></option>
                          <option value="18" label="18%"></option>
                          <option value="28" label="28%"></option>
                        </datalist>
                      </div>
                    </div>

                    <div className="col-lg-12 hstack">
                      <div className="col-3">
                        <label htmlFor="placeOfSupplySelect">Place of supply</label>
                      </div>
                      <div className="col-9 mt-1">
                        <select
                          id="placeOfSupplySelect"
                          className="form-control"
                          value={placeOfSupply}
                          onChange={(e) => setPlaceOfSupply(e.target.value)}
                        >
                          <option value="">Select</option>
                          {SOUTH_INDIA_PLACE_OF_SUPPLY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-12">
                <div className="card shadow-lg">
                  <div className="card-body">
                    <div className="table-wrapper">
                      <table className="table">
                        <thead>
                          <tr>
                            <th className="text-truncate align-middle">S.No</th>
                            <th className="text-truncate align-middle">
                              Product Name
                            </th>
                            <th className="text-truncate align-middle">
                              Part Number
                            </th>
                            {!hiddenGst && (
                              <th className="text-truncate align-middle">
                                GST%
                              </th>
                            )}
                            <th className="text-truncate align-middle">
                              M.R.P
                            </th>
                            <th className="text-truncate align-middle">Rate</th>
                            <th className="text-truncate align-middle">QTY</th>
                            {/* <th className="text-truncate align-middle">
                              DISCOUNT
                            </th> */}
                            {/*<th className="text-truncate align-middle">PD %</th>
                                                        <th className="text-truncate align-middle">
                                                            PD.Rs
                                                        </th> */}
                            {hiddenGst && (
                              <>
                              <th className="text-truncate align-middle">
                                  GST %
                                </th>
                                <th className="text-truncate align-middle">
                                  GST VALUE
                                </th>

                                <th className="text-truncate align-middle">
                                  Taxable
                                </th>
                              </>
                            )}
                            <th className="text-truncate align-middle">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesItems.map((pi, i) => {
                            return (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td className="p-0 d-flex">
                                  <button
                                    className="btn btn-sm py-0 px-1"
                                    onClick={() => {
                                      const updatedItems = salesItems.filter(
                                        (_, index) => index !== i,
                                      );
                                      setSalesItems(updatedItems);
                                    }}
                                  >
                                    X
                                  </button>
                                  <ProductSearch
                                    key={pi.id}
                                    editProductName={
                                      FormTypeList.EDIT ? pi.productName : null
                                    }
                                    handleProductName={handleProductName}
                                    pi={pi}
                                    resetSearch={resetSearch}
                                    setResetSearch={setResetSearch} // Pass the setResetSearch function
                                    handleKeyDown={handleKeyDown}
                                    refs={refs}
                                  />
                                </td>
                                <td className="p-0">
                                  <FormInput
                                    name="modelNumber"
                                    label=""
                                    labelClassName="required"
                                    value={pi.modelNumber}
                                    onChange={(e) =>
                                      handlemodelNumber(
                                        e.target.value,
                                        pi.id.toString(),
                                      )
                                    }
                                    errorText={productNameError}
                                    readOnly={true}
                                    containerClass="w-100 read-only-field"
                                  />
                                </td>
                                {!hiddenGst && (
                                  <td className="p-0">
                                    <FormInput
                                      name="gst"
                                      label=""
                                      labelClassName="required"
                                      value={pi.gstPercent}
                                      onChange={(e) =>
                                        handleGst(
                                          e.target.value,
                                          pi.id.toString(),
                                        )
                                      }
                                      errorText={gstError}
                                      readOnly={true}
                                      containerClass="read-only-field"
                                    />
                                  </td>
                                )}
                                <td className="p-0">
                                  <FormInput
                                    name="mrp"
                                    label=""
                                    labelClassName="required"
                                    value={pi.mrp}
                                    // onChange={(e) =>
                                    //     handleRate(
                                    //         e.target.value,
                                    //         pi.id.toString()
                                    //     )
                                    // }
                                    containerClass="read-only-field"
                                    type="number"
                                    readOnly={true}
                                  />
                                </td>
                                <td className="p-0">
                                  <FormInput
                                    name="Rate"
                                    label=""
                                    labelClassName="required"
                                    value={pi.rate}
                                    onChange={(e) =>
                                      handleRate(
                                        e.target.value,
                                        pi.id.toString(),
                                      )
                                    }
                                    type="number"
                                  />
                                </td>

                                <td className="p-0">
                                  <FormInput
                                    name={`qty-${pi.id}`}
                                    label=""
                                    labelClassName="required"
                                    value={pi.qty}
                                    onChange={(e) =>
                                      handleQty(
                                        e.target.value,
                                        pi.id.toString(),
                                      )
                                    }
                                    errorText={gstError}
                                    containerClass=""
                                    // type="number"
                                    ref={(el) =>
                                      (refs.current[`qty-${pi.id}`] = el)
                                    }
                                    onKeyDown={(e) =>
                                      handleKeyDown(
                                        e,
                                        "productName",
                                        pi.id.toString(),
                                        true,
                                      )
                                    }
                                    className={
                                      focusedField === `qty-${pi.id}`
                                        ? "border border-2 border-primary rounded"
                                        : ""
                                    }
                                  />
                                </td>

                                {/* <td className="p-0">
                                  <FormInput
                                    name="Discount"
                                    label=""
                                    labelClassName="required"
                                    value={pi.disAmt}
                                    onChange={(e) =>
                                      handleDiscountAmt(
                                        e.target.value,
                                        pi.id.toString(),
                                      )
                                    }
                                    containerClass=""
                                    type="number"
                                    readOnly={false}
                                  />
                                </td> */}
                                {/*   <td className="p-0">
                                                                    <FormInput
                                                                        name="pdPercent"
                                                                        label=""
                                                                        labelClassName="required"
                                                                        value={pi.pdPercent}
                                                                        onChange={(e) =>
                                                                            handlePDPercentChange(
                                                                                e.target.value,
                                                                                pi.id.toString()
                                                                            )
                                                                        }
                                                                        type="number"
                                                                        ref={(el) =>
                                                                            (refs.current[`pdPercent-${pi.id}`] = el)
                                                                        }
                                                                        onKeyDown={(e) =>
                                                                            handleKeyDown(
                                                                                e,
                                                                                "pdRupees",
                                                                                pi.id.toString()
                                                                            )
                                                                        }
                                                                        className={
                                                                            focusedField === `pdPercent-${pi.id}`
                                                                                ? "border border-2 border-primary rounded"
                                                                                : ""
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="p-0">
                                                                    <FormInput
                                                                        name="pdRupees"
                                                                        label=""
                                                                        labelClassName="required"
                                                                        value={pi.pdAmount}
                                                                        onChange={(e) =>
                                                                            handlePDAmountChange(
                                                                                e.target.value,
                                                                                pi.id.toString()
                                                                            )
                                                                        }
                                                                        type="number"
                                                                        ref={(el) =>
                                                                            (refs.current[`pdRupees-${pi.id}`] = el)
                                                                        }
                                                                        onKeyDown={(e) =>
                                                                            handleKeyDown(
                                                                                e,
                                                                                "productName",
                                                                                pi.id.toString(),
                                                                                true
                                                                            )
                                                                        }
                                                                        className={
                                                                            focusedField === `pdRupees-${pi.id}`
                                                                                ? "border border-2 border-primary rounded"
                                                                                : ""
                                                                        }
                                                                    />
                                                                </td> */}
                                {hiddenGst && (
                                  <>
                                  <td className="p-0">
                                      <FormInput
                                        name="GST %"
                                        label=""
                                        labelClassName="required"
                                        value={pi.gstPercent}
                                        onChange={(e) =>
                                          handleGSTValue(
                                            e.target.value,
                                            pi.id.toString(),
                                          )
                                        }
                                        containerClass=" read-only-field"
                                        readOnly={true}
                                      />
                                    </td>
                                    <td className="p-0">
                                      <FormInput
                                        name="GST VALUE"
                                        label=""
                                        labelClassName="required"
                                        value={pi.gstValue}
                                        onChange={(e) =>
                                          handleGSTValue(
                                            e.target.value,
                                            pi.id.toString(),
                                          )
                                        }
                                        containerClass=" read-only-field"
                                        readOnly={true}
                                      />
                                    </td>
                                    <td className="p-0">
                                      <FormInput
                                        name="taxable"
                                        label=""
                                        labelClassName="required"
                                        value={parseFloat(pi.taxable!).toFixed(
                                          2,
                                        )}
                                        onChange={(e) =>
                                          handleTaxable(
                                            e.target.value,
                                            pi.id.toString(),
                                          )
                                        }
                                        errorText={gstError}
                                        containerClass=" read-only-field"
                                        readOnly={true}
                                      />
                                    </td>
                                  </>
                                )}
                                <td className="p-0">
                                  <FormInput
                                    name="TotalAmt"
                                    label=""
                                    labelClassName="required"
                                    value={parseFloat(pi.totalAmt!).toFixed(2)}
                                    onChange={(e) =>
                                      handleTotAmt(
                                        e.target.value,
                                        pi.id.toString(),
                                      )
                                    }
                                    errorText={gstError}
                                    containerClass="w-100 read-only-field"
                                    readOnly={true}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div
                      className="d-flex justify-content-between hstack gap-1 mt-2"
                      style={{ padding: "0 45px" }}
                    >
                      <div
                        className="d-flex justify-content-between"
                        style={{ width: "58%" }}
                      >
                        {/* <h5 style={{ color: "red" }}>Total Items: {salesItems.filter(f =>
                                                f.productName?.label && f.productName?.value
                                            ).length}</h5> */}
                        <h5 style={{ color: "red" }}>
                          Total Items: {totalItems}
                        </h5>
                        <h5 style={{ color: "green" }}>
                          Total Quantity:{" "}
                          {salesItems.reduce(
                            (total, item) =>
                              total + parseFloat(item.qty || "0"),
                            0,
                          )}
                        </h5>
                      </div>

                      {/* <button className="btn btn-secondary" onClick={() => resetStates()}>Clear</button> */}
                      <div>
                        <button
                          className="btn btn-primary"
                          onClick={handleView}
                        >
                          View
                        </button>
                        {/* <button className="btn btn-primary" onClick={handleSave}>{formType === FormTypeList.ADD ? 'Save' : 'Update'}</button> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            {/* <div className="card shadow-lg ">
                        <div className="card-body"> */}
            <p>
              <span className="fs-4 badge bg-primary">
                {LocalFormatDate(billDate?.toDateString())}
              </span>
              {/* <span className="fs-4 badge bg-primary ms-1">{billTime}</span> */}
              <Clock />
            </p>
            <p>
              <span className="fs-3 badge bg-danger">Bill No: {billNo}</span>
            </p>

            <div className="card shadow-lg ">
              <div className="card-body">
                <div>
                  <h4>Total Amt: {TotalBillAmt}</h4>
                </div>
                <div>
                  <h4>Total Dis: {TotalDisAmt}</h4>
                </div>
                <div className="row">
                  <h4 className="text-center">Grand Total</h4>
                  <h2
                    className="text-center text-white "
                    style={{ backgroundColor: "#032566" }}
                  >
                    {rupeeFormat(finalAmount)}
                  </h2>
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <label>Total Items</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="totalItems"
                            label=""
                            labelClassName="required"
                            value={totalItems}
                            onChange={handleTotalItems}
                            errorText={totalItemsError}
                            containerClass="w-100 read-only-field"
                            type="number"
                            readOnly={true}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <label>Total Quantity</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="totalQty"
                            label=""
                            labelClassName="required"
                            value={totalQty}
                            onChange={handleTotalQty}
                            errorText={totalQtyError}
                            containerClass="w-100 read-only-field"
                            type="number"
                            readOnly={true}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <label>Pack No</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="packNo"
                            label=""
                            labelClassName="required"
                            value={packNo}
                            onChange={handlePackNo}
                            errorText={packNoError}
                            containerClass="w-100 read-only-field"
                            type="number"
                            readOnly={true}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <label>Your Savings</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="yourSavings"
                            label=""
                            labelClassName="required"
                            value={yourSavings}
                            onChange={handleYourSavings}
                            errorText={yourSavingsError}
                            containerClass="w-100 read-only-field"
                            type="number"
                            readOnly={true}
                          />
                        </td>
                      </tr>
                      {!hiddenGst && (
                        <>
                          <tr>
                            <td>
                              <label>NET IGST</label>
                            </td>
                            <td className="p-0">
                              <FormInput
                                name="netIgst"
                                label=""
                                labelClassName="required"
                                value={netIgst}
                                onChange={handleNetIgst}
                                errorText={netIgstError}
                                containerClass="w-100 read-only-field"
                                type="number"
                                readOnly={true}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <label>NET CGST</label>
                            </td>
                            <td className="p-0">
                              <FormInput
                                name="netCgst"
                                label=""
                                labelClassName="required"
                                value={netCgst}
                                onChange={handleNetCgst}
                                errorText={netCgstError}
                                containerClass="w-100 read-only-field"
                                type="number"
                                readOnly={true}
                              />
                            </td>
                          </tr>
                          <tr>
                            <td>
                              <label>NET SGST</label>
                            </td>
                            <td className="p-0">
                              <FormInput
                                name="netSgst"
                                label=""
                                labelClassName="required"
                                value={netSgst}
                                onChange={handleNetSgst}
                                errorText={netSgstError}
                                containerClass="w-100 read-only-field"
                                type="number"
                                readOnly={true}
                              />
                            </td>
                          </tr>

                          <tr>
                            <td>
                              <label>Net Taxable</label>
                            </td>
                            <td className="p-0">
                              <FormInput
                                name="netTaxable"
                                label=""
                                labelClassName="required"
                                value={netTaxable}
                                onChange={handleNetTaxable}
                                errorText={netTaxableError}
                                containerClass="read-only-field"
                                type="number"
                                readOnly={true}
                              />
                            </td>
                          </tr>
                        </>
                      )}
                      {!hiddenGst && (
                        <tr>
                          <td>
                            <label>Net GST Value</label>
                          </td>
                          <td className="p-0">
                            <FormInput
                              name="netGSTValue"
                              label=""
                              labelClassName="required"
                              value={netGSTValue}
                              onChange={handleNetGST}
                              errorText={netGSTValueError}
                              containerClass="read-only-field"
                              type="number"
                              readOnly={true}
                            />
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>
                          <label>Add</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="Add"
                            label=""
                            labelClassName="required"
                            value={add}
                            onChange={handleAdd}
                            errorText={addError}
                            containerClass="w-100"
                            type="number"
                            ref={addRef}
                            onKeyDown={(e) =>
                              handleKeyDownForRight(e, lessRef, "less")
                            }
                            className={
                              focusedField === `add`
                                ? "border border-2 border-primary rounded"
                                : ""
                            }
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <label>Less</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="Less"
                            label=""
                            labelClassName="required"
                            value={less}
                            onChange={handleLess}
                            errorText={lessError}
                            containerClass="w-100"
                            type="number"
                            ref={lessRef}
                            onKeyDown={(e) =>
                              handleKeyDownForRight(e, lessRef, "less")
                            }
                            className={
                              focusedField === `less`
                                ? "border border-2 border-primary rounded"
                                : ""
                            }
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <label>Net Amt</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="Net Amt"
                            label=""
                            labelClassName="required"
                            value={netAmt}
                            onChange={handleNetAmt}
                            containerClass="read-only-field"
                            errorText={lessError}
                            // containerClass="w-100"
                            readOnly={true}
                            type="number"
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <label>Round Off</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="RoundOff"
                            label=""
                            labelClassName="required"
                            value={roundOff}
                            onChange={handleRoundOff}
                            errorText={roundOffError}
                            containerClass="read-only-field"
                            type="number"
                            readOnly={true}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <label>Grand Total</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="grandTotal"
                            label=""
                            labelClassName="required"
                            value={gtotal}
                            onChange={handleGrandTotal}
                            errorText={gtotalError}
                            containerClass="w-100 read-only-field"
                            readOnly={true}
                            type="number"
                          />
                        </td>
                      </tr>

                      {/* <tr>
                                            <td><label>Amt In Words</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='AmtInWords'
                                                    label=''
                                                    labelClassName="required"
                                                    value={amtInWords}
                                                    onChange={handleAmtInWords}
                                                    errorText={amtInWordsError}
                                                    containerClass="w-100"
                                                // type="number"
                                                />
                                            </td>
                                        </tr> */}
                      {/* <tr>
                                            <td><label>Payment Mode</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='paymentMode'
                                                    label=''
                                                    labelClassName="required"
                                                    value={paymentMode}
                                                    onChange={handlePaymentMode}
                                                    errorText={paymentModeError}
                                                    containerClass="w-100"
                                                    type="select"
                                                >
                                                    <option value="">Select</option>
                                                    {SALSE_PAYMENT_MODES.map((a, i) => {
                                                        return <Option value={a.value.toString()} key={i}>{a.text}</Option>
                                                    })}
                                                </FormInput>
                                            </td>
                                        </tr> */}
                      {/* {paymentMode === SalesPaymentModesDropDown.BANK && <tr>
                                            <td><label>Bank</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='bankNumber'
                                                    label=''
                                                    labelClassName="required"
                                                    value={bankNumber}
                                                    onChange={handleBank}
                                                    containerClass="w-100"
                                                    type="select"
                                                >
                                                    <option value="">Select</option>
                                                    {AccountLists.filter((al) => al.groupName === 'main' && al.accountName?.toLowerCase().includes('bank')).map((al) => {
                                                        return <option value={al.accountNumber}>{al.accountName}</option>
                                                    })
                                                    }
                                                </FormInput>
                                            </td>
                                        </tr>} */}
                      <tr>
                        <td>
                          <label>Amt Paid</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="AmtPaid"
                            label=""
                            labelClassName="required"
                            value={amtPaid}
                            onChange={handleAmtPaid}
                            errorText={amtPaidError}
                            containerClass="w-100 read-only-field"
                            type="number"
                            readOnly={true}
                          />
                        </td>
                      </tr>
                      {/* <tr>
                                            <td><label>Balance</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='Balance'
                                                    label=''
                                                    labelClassName="required"
                                                    value={balance}
                                                    onChange={handleBalance}
                                                    errorText={balanceError}
                                                    containerClass="w-100 read-only-field"
                                                    type="number"
                                                    readOnly={true}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><label>Ten.Amt</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='tenderedAmt'
                                                    label=''
                                                    labelClassName="required"
                                                    value={tenderedAmt}
                                                    onChange={handleTenderedAmt}
                                                    errorText={tenderedAmtError}
                                                    containerClass="w-100 read-only-field"
                                                    type="number"
                                                    readOnly={true}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><label>Ten.Return</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='tenderedReturn'
                                                    label=''
                                                    labelClassName="required"
                                                    value={tenderedReturn}
                                                    onChange={handleTenderedReturn}
                                                    errorText={tenderedReturnError}
                                                    containerClass="w-100 read-only-field"
                                                    type="number"
                                                    readOnly={true}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><label>Tendered Bal</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='tenderedBal'
                                                    label=''
                                                    labelClassName="required"
                                                    value={tenderedBal}
                                                    onChange={handleTenderedBal}
                                                    errorText={tenderedBalError}
                                                    containerClass="w-100 read-only-field"
                                                    type="number"
                                                    readOnly={true}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><label>Current Bal</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='currentBal'
                                                    label=''
                                                    labelClassName="required"
                                                    value={currentBal}
                                                    onChange={() => { }}
                                                    errorText={currentBalError}
                                                    containerClass="w-100 read-only-field"
                                                    type="number"
                                                    readOnly={true}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><label>Previous Bal</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='previousBal'
                                                    label=''
                                                    labelClassName="required"
                                                    value={previousBal}
                                                    onChange={() => { }}
                                                    errorText={previousBalError}
                                                    containerClass="w-100 read-only-field"
                                                    type="number"
                                                    readOnly={true}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><label>OverAll Bal</label></td>
                                            <td className="p-0">
                                                <FormInput
                                                    name='overAllBal'
                                                    label=''
                                                    labelClassName="required"
                                                    value={overAllBal}
                                                    onChange={() => { }}
                                                    errorText={overAllBalError}
                                                    containerClass="w-100 read-only-field"
                                                    type="number"
                                                    readOnly={true}
                                                />
                                            </td>
                                        </tr> */}
                      <tr>
                        <td>
                          <label>Previous Points</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="previouspoints"
                            label=""
                            labelClassName="required"
                            value={previousPoints}
                            onChange={handleTenderedBal}
                            // errorText={previouspointsError}
                            containerClass="w-100 read-only-field"
                            type="number"
                            readOnly={true}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <label>Points Earned</label>
                        </td>
                        <td className="p-0">
                          <FormInput
                            name="pointsEarned"
                            label=""
                            labelClassName="required"
                            value={pointsEarned}
                            onChange={handleTenderedBal}
                            // errorText={pointsEarnedError}
                            containerClass="w-100 read-only-field"
                            type="number"
                            readOnly={true}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                 <div className="d-flex justify-content-end mt-2">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      void (async () => {
                        let skipBrowserPrint = false;
                        try {
                          const settings = await getSettings();
                          const branchThermalId =
                            Number(AuthUser?.branchId) || 0;
                          skipBrowserPrint = isThermalPrinterFromSettings(
                            settings,
                            branchThermalId
                          );
                        } catch {
                          skipBrowserPrint = false;
                        }
                        if (!skipBrowserPrint) {
                          handlePrintDirectly();
                        }
                        handleSave();
                      })();
                    }}
                  /* 
                  🔽 OLD CODE (Commented out for safety)
                  onClick={() => {
                    if (parseFloat(gtotal) > 0) {
                      setShowTenderModal(true);
                    } else {
                      toast("Please fill the items first", ToastTypes.ERROR);
                    }
                  }} 
                  */
                  >
                    Pay Bill
                  </button>
                </div>
              </div>
            </div>
            <p>
              <span className="fs-4 badge bg-primary">User: {billedBy}</span>
              <span className="fs-4 badge bg-primary ms-1">Sys: {sysNo}</span>
            </p>
          </div>
        </div>
      </UserLayout>

      <Modal
        headerText="Payment Particulars"
        onClose={handleFormClose}
        centered
        size="lg"
        visible={showTenderModal}
      >
        <div style={{ paddingTop: -20 }}>
          <Form className="row">
            <Form.Check
              type="radio"
              label="Cash/Bank"
              value="cashAndBank"
              checked={paymentMethod === "cashAndBank"}
              onChange={(e) => setPaymentMethod(e.target.value)}
              style={{ fontWeight: "bold", fontSize: "18px" }}
              className="col-4 mb-1  ms-2"
            />
            <Form.Check
              type="radio"
              label="Credit"
              value="credit"
              checked={paymentMethod === "credit"}
              onChange={(e) => setPaymentMethod(e.target.value)}
              style={{ fontWeight: "bold", fontSize: "18px" }}
              className="col-4 mb-1  ms-2"
            />
            {/* <div className="d-flex justify-content-end"> */}
            {paymentMethod === "cashAndBank" && (
              <h4 className="col-3 mb-1 ms-1 badge bg-danger fs-4">
                Grand Total: {gtotal}
              </h4>
            )}
            {/* </div> */}
          </Form>
          {paymentMethod === "cashAndBank" && (
            <div className="">
              <table
                className="table "
                style={{ width: "100%", border: "5px" }}
              >
                <tbody>
                  <tr>
                    <td style={{ width: "40%" }}>
                      <strong>Grand Total</strong>
                    </td>
                    <td style={{ padding: "0", width: "60%" }}>
                      <FormInput
                        className="fw-bold"
                        type="number"
                        name="grandTotal"
                        value={gtotal}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF", width: "100%" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Previous Balance</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        className="fw-bold"
                        type="number"
                        name="previousBalance"
                        value={parseFloat(previousBal).toFixed(2)}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Total Balance</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        className="fw-bold"
                        type="number"
                        name="totalBalance"
                        value={totalBal}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Tender Cash Amount</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        // className="fw-bold"
                        type="number"
                        name="tenderedAmt"
                        value={tenderedAmt}
                        onChange={handleNonNegativeNumberInput(setTenderedAmt)}
                        ref={tenderRef}
                        onKeyDown={(e) =>
                          handleKeyDownForRight(e, card1Ref, "card1")
                        }
                        className={
                          focusedField === "tenderAmt"
                            ? "border border-2 border-primary rounded"
                            : "fw-bold"
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Cash (C)</strong>
                    </td>
                    <td
                      style={{
                        padding: "0",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Form.Control
                        type="number"
                        className="fw-bold"
                        name="cash"
                        value={cash}
                        // readOnly={!cashEditable}
                        readOnly={true}
                        style={{
                          backgroundColor: cashEditable ? "" : "#DBF3DF",
                          flex: 1,
                        }}
                        onChange={
                          cashEditable
                            ? handleNonNegativeNumberInput(setCash)
                            : undefined
                        }
                      />
                      {/* <button
                                                type="button"
                                                className=" btn btn-sm fw-bold btn-info p-1"
                                                onClick={() => setCashEditable((prev) => !prev)}
                                            >
                                                <i className="fe-edit noti-icon"></i>
                                            </button> */}
                    </td>
                  </tr>
                </tbody>
              </table>
              <table
                className="table "
                style={{ width: "100%", border: "5px" }}
              >
                <tbody>
                  <tr>
                    <td style={{ width: "40%" }}>
                      <strong>Card 1 (C1)</strong>
                    </td>
                    <td style={{ padding: "0", width: "60%" }}>
                      <FormInput
                        // className="fw-bold"
                        type="number"
                        name="card1"
                        value={card1}
                        onChange={handleNonNegativeNumberInput(setCard1)}
                        ref={card1Ref}
                        onKeyDown={(e) =>
                          handleKeyDownForRight(e, card2Ref, "card2")
                        }
                        className={
                          focusedField === "card1"
                            ? "border border-2 border-primary rounded"
                            : "fw-bold"
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Card 2 (C2)</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        // className="fw-bold"
                        type="number"
                        name="card2"
                        value={card2}
                        onChange={handleNonNegativeNumberInput(setCard2)}
                        ref={card2Ref}
                        onKeyDown={(e) =>
                          handleKeyDownForRight(e, online1Ref, "onlinePayment1")
                        }
                        className={
                          focusedField === "card2"
                            ? "border border-2 border-primary rounded"
                            : "fw-bold"
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Online Payment 1 (C3)</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        // className="fw-bold"
                        type="number"
                        name="onlinePayment1"
                        value={onlinePayment1}
                        onChange={handleNonNegativeNumberInput(
                          setOnlinePayment1,
                        )}
                        ref={online1Ref}
                        onKeyDown={(e) =>
                          handleKeyDownForRight(e, online1Ref, "onlinePayment2")
                        }
                        className={
                          focusedField === "onlinePayment1"
                            ? "border border-2 border-primary rounded"
                            : "fw-bold"
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Online Payment 2 (C4)</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        // className="fw-bold"
                        type="number"
                        name="onlinePayment2"
                        value={onlinePayment2}
                        onChange={handleNonNegativeNumberInput(
                          setOnlinePayment2,
                        )}
                        ref={online2Ref}
                        onKeyDown={(e) =>
                          handleKeyDownForRight(e, tenderRef, "tenderAmt")
                        }
                        className={
                          focusedField === "onlinePayment2"
                            ? "border border-2 border-primary rounded"
                            : "fw-bold"
                        }
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <table
                className="table "
                style={{ width: "100%", border: "5px solid black" }}
              >
                <tbody>
                  <tr>
                    <td style={{ width: "40%" }}>
                      <strong>Credit</strong>
                    </td>
                    <td style={{ padding: "0", width: "60%" }}>
                      <FormInput
                        className="fw-bold"
                        type="number"
                        name="balance"
                        value={parseFloat(balance).toFixed(2)}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Split Payment (Y/N)</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        className="fw-bold"
                        name="splitPayment"
                        value={split}
                        onChange={(e) => setSplit(e.target.value)}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Amount Paid</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        className="fw-bold"
                        type="number"
                        name="amtPaid"
                        value={amtPaid}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Tender Return</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        className="fw-bold"
                        type="number"
                        name="tenderedReturn"
                        value={parseFloat(tenderedReturn).toFixed(2)}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                  <tr className="readonly-row">
                    <td>
                      <strong>Remark</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        className="fw-bold"
                        name="remark"
                        value={remark}
                        readOnly
                        onChange={(e) => setRemark(e.target.value)}
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Ledger Balance</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        className="fw-bold"
                        type="number"
                        name="ledgerBal"
                        value={parseFloat(ledgerBal).toFixed(2)}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Bank Recevied</strong>
                    </td>
                    <td style={{ padding: "0" }}>
                      <FormInput
                        className="fw-bold"
                        type="number"
                        name="backRecevied"
                        value={bankRecevied}
                        readOnly
                        style={{ backgroundColor: "#DBF3DF" }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div className="d-flex justify-content-end">
            <Button
              className="btn btn-sm fw-bold btn-secondary fw-bold"
              onClick={() => resetBill()}
              style={{ marginRight: "10px" }}
            >
              Clear
            </Button>
            {/* <Button variant="primary" onClick={handleSave}>Save Changes</Button> */}
            <button className="btn btn-primary" onClick={handleSave}>
              {formType === FormTypeList.ADD ? "Save Bill" : "Update Bill"}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        headerText="Choose your option"
        onClose={handleClose}
        centered
        size="lg"
        visible={confirmModal}
      >
        <div className="d-flex align-items-center gap-4">
          <label>
            <input
              type="radio"
              name="printOption"
              value="print"
              checked={options === "print"}
              onChange={(e) => setOptions(e.target.value)}
            />
            Print
          </label>

          <label>
            <input
              type="radio"
              name="printOption"
              value="download"
              checked={options === "download"}
              onChange={(e) => setOptions(e.target.value)}
            />
            Download PDF
          </label>

          <button
            className="btn btn-primary btn-sm"
            onClick={handleOptionSubmit}
            disabled={!options}
          >
            Submit
          </button>
        </div>
      </Modal>
    </>
  );
};
export default SalesMaster;
